import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { showQuotaExceededAlert } from "../utils/quotaAlert";
import {
  Package,
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SortAsc,
  SortDesc,
  RefreshCw,
  Image as ImageIcon,
  Plus,
  Minus,
  ImagePlus,
  FileText,
  Tag,
  FolderTree,
  Sliders,
  Layout,
  Truck,
  Link2,
  Type,
  Trash2,
  Upload,
  Undo2,
} from "lucide-react";
import { getAuthData } from "../utils/auth";
import { getStoreId } from "../utils/storage";
import * as logger from "../utils/logger";
import { showToast as addToast } from "../utils/toast";

function escapeHtml(text) {
  if (text == null) return "";
  const s = String(text);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];
const SORT_OPTIONS = [
  { value: "", labelKey: "bulkProductUpdate.sortDefault" },
  { value: "name", labelKey: "bulkProductUpdate.sortName" },
  { value: "price", labelKey: "bulkProductUpdate.sortPrice" },
  { value: "created", labelKey: "bulkProductUpdate.sortCreated" },
];

const FILTER_TYPES = [
  { id: "keyword", labelKey: "bulkProductUpdate.filterByKeyword", valueLabelKey: "bulkProductUpdate.filterValueKeyword", placeholderKey: "bulkProductUpdate.filterPlaceholderKeyword" },
  { id: "sku", labelKey: "bulkProductUpdate.filterBySku", valueLabelKey: "bulkProductUpdate.filterValueSku", placeholderKey: "bulkProductUpdate.filterPlaceholderSku" },
  { id: "price", labelKey: "bulkProductUpdate.filterByPrice", valueLabelKey: "bulkProductUpdate.filterValuePrice", placeholderKey: null },
  { id: "availability", labelKey: "bulkProductUpdate.filterByAvailability", valueLabelKey: "bulkProductUpdate.filterValueAvailability", placeholderKey: null },
  { id: "enableDisable", labelKey: "bulkProductUpdate.filterByEnableDisable", valueLabelKey: "bulkProductUpdate.filterByEnableDisable", placeholderKey: null },
  { id: "category", labelKey: "bulkProductUpdate.filterByCategory", valueLabelKey: "bulkProductUpdate.filterValueCategory", placeholderKey: null },
  { id: "attribute", labelKey: "bulkProductUpdate.filterByAttribute", valueLabelKey: "bulkProductUpdate.filterByAttribute", placeholderKey: null },
  { id: "creationDate", labelKey: "bulkProductUpdate.filterByCreationDate", valueLabelKey: "bulkProductUpdate.filterByCreationDate", placeholderKey: null },
  { id: "modificationDate", labelKey: "bulkProductUpdate.filterByModificationDate", valueLabelKey: "bulkProductUpdate.filterByModificationDate", placeholderKey: null },
];

function buildChangeAlerts(propertySelected, propertyValues, t) {
  const alerts = [];
  if (propertySelected.price) {
    alerts.push({ key: "price", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propPrice"), value: propertyValues.price !== "" ? propertyValues.price : "0.00" }) });
  }
  if (propertySelected.compareToPrice) {
    alerts.push({ key: "compareToPrice", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propCompareToPrice"), value: propertyValues.compareToPrice !== "" ? propertyValues.compareToPrice : "0.00" }) });
  }
  if (propertySelected.weight) {
    alerts.push({ key: "weight", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propWeight"), value: propertyValues.weight !== "" ? propertyValues.weight : "0.00" }) });
  }
  if (propertySelected.dimensions) {
    const l = propertyValues.length !== "" ? propertyValues.length : "0";
    const w = propertyValues.width !== "" ? propertyValues.width : "0";
    const h = propertyValues.height !== "" ? propertyValues.height : "0";
    const d = `${t("bulkProductUpdate.propDimLength")} ${l} × ${t("bulkProductUpdate.propDimWidth")} ${w} × ${t("bulkProductUpdate.propDimHeight")} ${h}`;
    alerts.push({ key: "dimensions", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propDimensions"), value: d }) });
  }
  if (propertySelected.quantity) {
    if (propertyValues.unlimited) {
      alerts.push({ key: "unlimited", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propUnlimited"), value: t("bulkProductUpdate.yes") }) });
    } else {
      alerts.push({ key: "quantity", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propQuantity"), value: propertyValues.quantity !== "" ? propertyValues.quantity : "0" }) });
    }
  }
  if (propertySelected.stockWarning) {
    alerts.push({ key: "stockWarning", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propStockWarning"), value: propertyValues.stockWarning !== "" ? propertyValues.stockWarning : "0" }) });
  }
  if (propertySelected.enabled) {
    alerts.push({ key: "enabled", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propEnabled"), value: propertyValues.enabled ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no") }) });
  }
  if (propertySelected.shippingRequired) {
    alerts.push({ key: "shippingRequired", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propShippingRequired"), value: propertyValues.shippingRequired ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no") }) });
  }
  if (propertySelected.productType) {
    const typeLabel = propertyValues.productType === "digital" ? t("bulkProductUpdate.propTypeDigital") : t("bulkProductUpdate.propTypeGeneral");
    alerts.push({ key: "productType", msg: t("bulkProductUpdate.alertChangeTo", { property: t("bulkProductUpdate.propProductType"), value: typeLabel }) });
  }
  return alerts;
}

function onlyNumericInput(value) {
  if (value === "" || value === undefined) return value;
  const s = String(value).replace(/[^0-9.]/g, "");
  const parts = s.split(".");
  if (parts.length <= 2) return s;
  return parts[0] + "." + parts.slice(1).join("");
}

function integerOnlyInput(value) {
  if (value === "" || value === undefined) return value;
  const s = onlyNumericInput(value);
  if (s === "" || s === ".") return s === "." ? "" : s;
  return s.split(".")[0];
}

function clampNonNegative(value) {
  if (value === "" || value === undefined) return value;
  const n = parseFloat(String(value).replace(/,/g, ""), 10);
  if (Number.isNaN(n) || n < 0) return "0";
  return value;
}

function roundToMax2Decimals(value) {
  if (value === "" || value === undefined) return value;
  const n = parseFloat(String(value).replace(/,/g, ""), 10);
  if (Number.isNaN(n)) return value;
  const rounded = Math.round(n * 100) / 100;
  return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(2).replace(/\.?0+$/, "");
}

/** Allows typing numbers with up to 2 decimal places without rounding on each keystroke. */
function max2DecimalsInput(value) {
  if (value === "" || value === undefined) return value;
  const s = onlyNumericInput(value);
  const parts = s.split(".");
  if (parts.length === 1) return s;
  if (parts[1].length <= 2) return s;
  return parts[0] + "." + parts[1].slice(0, 2);
}

function getPropertyChangeSummary(key, propertyValues, t) {
  const v = propertyValues;
  switch (key) {
    case "price":
      return v.price !== "" ? v.price : "0.00";
    case "compareToPrice":
      return v.compareToPrice !== "" ? v.compareToPrice : "0.00";
    case "weight":
      return v.weight !== "" ? v.weight : "0.00";
    case "dimensions": {
      const l = v.length !== "" ? v.length : "0";
      const w = v.width !== "" ? v.width : "0";
      const h = v.height !== "" ? v.height : "0";
      return `${t("bulkProductUpdate.propDimLength")} ${l} × ${t("bulkProductUpdate.propDimWidth")} ${w} × ${t("bulkProductUpdate.propDimHeight")} ${h}`;
    }
    case "quantity":
      return v.unlimited ? t("bulkProductUpdate.propUnlimited") + ": " + t("bulkProductUpdate.yes") : (v.quantity !== "" ? v.quantity : "0");
    case "stockWarning":
      return v.stockWarning !== "" ? v.stockWarning : "0";
    case "enabled":
      return v.enabled ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no");
    case "shippingRequired":
      return v.shippingRequired ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no");
    case "productType":
      return v.productType === "digital" ? t("bulkProductUpdate.propTypeDigital") : t("bulkProductUpdate.propTypeGeneral");
    default:
      return null;
  }
}

/** Returns null if all selected properties/SEO have non-empty values; otherwise { fieldLabelKey } for first invalid. */
function getPropertiesValidationError(propertySelected, propertyValues, t) {
  const empty = (v) => String(v ?? "").trim() === "";
  const labelKeyFor = (key) => {
    const map = {
      price: "propPrice",
      compareToPrice: "propCompareToPrice",
      weight: "propWeight",
      dimensions: "propDimensions",
      quantity: "propQuantity",
      stockWarning: "propStockWarning",
      seoTitle: "propSeoTitle",
      seoDescription: "propSeoDescription",
    };
    return map[key] || key;
  };
  if (propertySelected.price && empty(propertyValues.price)) return { fieldLabelKey: labelKeyFor("price") };
  if (propertySelected.compareToPrice && empty(propertyValues.compareToPrice)) return { fieldLabelKey: labelKeyFor("compareToPrice") };
  if (propertySelected.weight && empty(propertyValues.weight)) return { fieldLabelKey: labelKeyFor("weight") };
  if (propertySelected.dimensions) {
    if (empty(propertyValues.length) || empty(propertyValues.width) || empty(propertyValues.height))
      return { fieldLabelKey: labelKeyFor("dimensions") };
  }
  if (propertySelected.quantity && !propertyValues.unlimited && empty(propertyValues.quantity))
    return { fieldLabelKey: labelKeyFor("quantity") };
  if (propertySelected.stockWarning && empty(propertyValues.stockWarning)) return { fieldLabelKey: labelKeyFor("stockWarning") };
  if (propertySelected.seoTitle && empty(propertyValues.seoTitle)) return { fieldLabelKey: labelKeyFor("seoTitle") };
  if (propertySelected.seoDescription && empty(propertyValues.seoDescription)) return { fieldLabelKey: labelKeyFor("seoDescription") };
  return null;
}

function getProductAttr(product, name) {
  const a = (product.attributes || []).find((x) => (x.name || "").toLowerCase() === name.toLowerCase());
  return (a && a.value != null ? String(a.value) : "") || "";
}

function resolveSeoTemplate(template, product, categories) {
  if (!template || typeof template !== "string") return "";
  const catId = product.defaultCategoryId ?? product.categoryIds?.[0];
  const category = categories.find((c) => Number(c.id) === Number(catId));
  const categoryName = category?.name ?? "";
  const price = product.price != null ? Number(product.price) : 0;
  const priceFormatted = Number.isFinite(price) ? `$${price.toFixed(2)}` : "";
  let desc = (product.description ?? "").trim();
  if (desc) {
    const div = typeof document !== "undefined" ? document.createElement("div") : null;
    if (div) {
      div.innerHTML = desc;
      desc = (div.textContent || div.innerText || "").trim().slice(0, 500);
    }
  }
  const productType = Number(product.productClassId) === 1 ? "Digital" : "General";
  return template
    .replace(/\{product_name\}/g, (product.name ?? "").trim())
    .replace(/\{sku\}/g, (product.sku ?? "").trim())
    .replace(/\{price\}/g, String(price))
    .replace(/\{price_formatted\}/g, priceFormatted)
    .replace(/\{description\}/g, desc)
    .replace(/\{category_name\}/g, categoryName)
    .replace(/\{brand\}/g, getProductAttr(product, "Brand"))
    .replace(/\{mpn\}/g, getProductAttr(product, "MPN"))
    .replace(/\{upc\}/g, getProductAttr(product, "UPC"))
    .replace(/\{condition\}/g, getProductAttr(product, "Condition"))
    .replace(/\{product_type\}/g, productType)
    .replace(/\{store_name\}/g, "");
}

const SEO_TAG_VARIABLES = [
  { labelKey: "variableProductName", value: "{product_name}" },
  { labelKey: "variableSku", value: "{sku}" },
  { labelKey: "variablePrice", value: "{price}" },
  { labelKey: "variablePriceFormatted", value: "{price_formatted}" },
  { labelKey: "variableDescription", value: "{description}" },
  { labelKey: "variableCategoryName", value: "{category_name}" },
  { labelKey: "variableBrand", value: "{brand}" },
  { labelKey: "variableMpn", value: "{mpn}" },
  { labelKey: "variableUpc", value: "{upc}" },
  { labelKey: "variableCondition", value: "{condition}" },
  { labelKey: "variableProductType", value: "{product_type}" },
  { labelKey: "variableStoreName", value: "{store_name}" },
];

const PROPERTIES_SECTION_KEYS = [
  "price",
  "compareToPrice",
  "weight",
  "dimensions",
  "quantity",
  "stockWarning",
  "enabled",
  "shippingRequired",
  "productType",
];

const INITIAL_PROPERTY_VALUES_FOR_SECTION = {
  price: "",
  compareToPrice: "",
  weight: "",
  length: "",
  width: "",
  height: "",
  quantity: "",
  unlimited: false,
  stockWarning: "",
  enabled: true,
  shippingRequired: true,
  productType: "general",
};

function PropertiesForm({
  t,
  propertySelected,
  propertyValues,
  setPropertyChecked,
  setPropertyValue,
  propertiesSelectedCount,
  onClearAll,
}) {
  const changeAlerts = buildChangeAlerts(propertySelected, propertyValues, t);

  const summaryPill = (summary) =>
    summary != null ? (
      <span
        className="small flex-shrink-0 px-2 py-1 rounded-pill text-nowrap"
        style={{
          background: "linear-gradient(135deg, rgba(255, 193, 7, 0.35) 0%, rgba(253, 126, 20, 0.2) 100%)",
          color: "#92400e",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      >
        {t("bulkProductUpdate.willChangeTo", { value: summary })}
      </span>
    ) : null;

  const row = (key, label, control, hint, changeSummary, summaryBelow) => {
    const summary = changeSummary ?? (propertySelected[key] ? getPropertyChangeSummary(key, propertyValues, t) : null);
    return (
      <div
        key={key}
        className="d-flex gap-3 py-2 px-3 rounded-2 flex-wrap"
        style={{
          backgroundColor: propertySelected[key] ? "rgba(255, 243, 205, 0.35)" : "rgba(255,255,255,0.7)",
          marginBottom: "4px",
          borderLeft: propertySelected[key] ? "3px solid rgba(253, 126, 20, 0.5)" : undefined,
          alignItems: summaryBelow ? "flex-start" : "center",
        }}
      >
        <input
          type="checkbox"
          id={`step2-prop-${key}`}
          className="form-check-input flex-shrink-0 mt-0"
          style={{ marginTop: summaryBelow ? "0.35rem" : undefined }}
          checked={!!propertySelected[key]}
          onChange={(e) => setPropertyChecked(key, e.target.checked)}
          aria-label={label}
        />
        <label
          htmlFor={`step2-prop-${key}`}
          className="mb-0 flex-shrink-0 text-dark"
          style={{ minWidth: "140px", fontSize: "0.9rem", cursor: "pointer" }}
        >
          {label}
        </label>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {summaryBelow ? (
            <>
              <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                {control}
                {hint && <span className="small text-muted flex-shrink-0">{hint}</span>}
              </div>
              {summary != null && <div className="mt-1">{summaryPill(summary)}</div>}
            </>
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {control}
                {hint && <span className="small text-muted">{hint}</span>}
                {summaryPill(summary)}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="properties-form">
      <div className="rounded-3 p-3 mb-3" style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold mb-2 small text-dark" style={{ fontSize: "0.85rem" }}>
          {t("bulkProductUpdate.propertiesKnowFirst")}
        </p>
        <ul className="mb-0 ps-3 small text-muted" style={{ fontSize: "0.8rem" }}>
          <li>{t("bulkProductUpdate.propertiesOnlySelected")}</li>
          <li>{t("bulkProductUpdate.propertiesAppliedAll")}</li>
        </ul>
      </div>

      <div className="rounded-3 overflow-hidden" style={{ border: "1px solid var(--bs-border-color)" }}>
        {onClearAll && propertiesSelectedCount > 0 && (
          <div className="d-flex justify-content-end py-2 px-3 border-bottom" style={{ backgroundColor: "rgba(248, 249, 250, 0.8)" }}>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClearAll}>
              {t("bulkProductUpdate.propertiesClearAll")}
            </button>
          </div>
        )}
        {row(
          "price",
          t("bulkProductUpdate.propPrice"),
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            style={{ maxWidth: "120px" }}
            placeholder="0.00"
            value={propertyValues.price}
            onChange={(e) => setPropertyValue("price", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
            disabled={!propertySelected.price}
          />
        )}
        {row(
          "compareToPrice",
          t("bulkProductUpdate.propCompareToPrice"),
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            style={{ maxWidth: "120px" }}
            placeholder="0.00"
            value={propertyValues.compareToPrice}
            onChange={(e) => setPropertyValue("compareToPrice", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
            disabled={!propertySelected.compareToPrice}
          />
        )}
        {row(
          "weight",
          t("bulkProductUpdate.propWeight"),
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            style={{ maxWidth: "120px" }}
            placeholder="0.00"
            value={propertyValues.weight}
            onChange={(e) => setPropertyValue("weight", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
            disabled={!propertySelected.weight}
          />
        )}
        {row(
          "dimensions",
          t("bulkProductUpdate.propDimensions"),
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="d-flex flex-column gap-1">
              <label className="form-label mb-0 small text-muted">{t("bulkProductUpdate.propDimLength")}</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control form-control-sm"
                style={{ width: "80px" }}
                placeholder="0"
                value={propertyValues.length}
                onChange={(e) => setPropertyValue("length", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
                disabled={!propertySelected.dimensions}
              />
            </div>
            <div className="d-flex flex-column gap-1">
              <label className="form-label mb-0 small text-muted">{t("bulkProductUpdate.propDimWidth")}</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control form-control-sm"
                style={{ width: "80px" }}
                placeholder="0"
                value={propertyValues.width}
                onChange={(e) => setPropertyValue("width", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
                disabled={!propertySelected.dimensions}
              />
            </div>
            <div className="d-flex flex-column gap-1">
              <label className="form-label mb-0 small text-muted">{t("bulkProductUpdate.propDimHeight")}</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control form-control-sm"
                style={{ width: "80px" }}
                placeholder="0"
                value={propertyValues.height}
                onChange={(e) => setPropertyValue("height", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
                disabled={!propertySelected.dimensions}
              />
            </div>
          </div>
        )}
        {row(
          "quantity",
          t("bulkProductUpdate.propQuantity"),
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              className="form-control form-control-sm"
              style={{ maxWidth: "80px" }}
              placeholder="0"
              value={propertyValues.quantity}
              onChange={(e) => setPropertyValue("quantity", clampNonNegative(integerOnlyInput(onlyNumericInput(e.target.value))))}
              disabled={!propertySelected.quantity || propertyValues.unlimited}
            />
            <label className="mb-0 small d-flex align-items-center gap-1">
              <input
                type="checkbox"
                className="form-check-input"
                checked={propertyValues.unlimited}
                onChange={(e) => setPropertyValue("unlimited", e.target.checked)}
                disabled={!propertySelected.quantity}
              />
              {t("bulkProductUpdate.propUnlimited")}
            </label>
          </div>
        )}
        {row(
          "stockWarning",
          t("bulkProductUpdate.propStockWarning"),
          <input
            type="text"
            inputMode="numeric"
            className="form-control form-control-sm"
            style={{ maxWidth: "80px" }}
            placeholder="0"
            value={propertyValues.stockWarning}
            onChange={(e) => setPropertyValue("stockWarning", clampNonNegative(integerOnlyInput(onlyNumericInput(e.target.value))))}
            disabled={!propertySelected.stockWarning}
          />
        )}
        {row(
          "enabled",
          t("bulkProductUpdate.propEnabled"),
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={propertyValues.enabled}
              onChange={(e) => setPropertyValue("enabled", e.target.checked)}
              disabled={!propertySelected.enabled}
            />
            <span className="small ms-1">{propertyValues.enabled ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no")}</span>
          </div>
        )}
        {row(
          "shippingRequired",
          t("bulkProductUpdate.propShippingRequired"),
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={propertyValues.shippingRequired}
              onChange={(e) => setPropertyValue("shippingRequired", e.target.checked)}
              disabled={!propertySelected.shippingRequired}
            />
            <span className="small ms-1">{propertyValues.shippingRequired ? t("bulkProductUpdate.yes") : t("bulkProductUpdate.no")}</span>
          </div>
        )}
        {row(
          "productType",
          t("bulkProductUpdate.propProductType"),
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: "160px" }}
            value={propertyValues.productType}
            onChange={(e) => setPropertyValue("productType", e.target.value)}
            disabled={!propertySelected.productType}
          >
            <option value="general">{t("bulkProductUpdate.propTypeGeneral")}</option>
            <option value="digital">{t("bulkProductUpdate.propTypeDigital")}</option>
          </select>
        )}
      </div>

      {/* Bottom summary stack – full sentence per change */}
      {changeAlerts.length > 0 && (
        <div className="mt-4">
          <p className="small fw-semibold text-muted mb-2 text-uppercase" style={{ letterSpacing: "0.05em" }}>
            {t("bulkProductUpdate.summaryOfChanges")}
          </p>
          <div className="d-flex flex-column gap-2">
            {changeAlerts.map((alert) => (
              <div
                key={alert.key}
                className="d-flex align-items-center gap-2 py-2 px-3 rounded-3 border-0 shadow-sm"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 193, 7, 0.18) 0%, rgba(253, 126, 20, 0.12) 100%)",
                  borderLeft: "4px solid rgba(253, 126, 20, 0.7)",
                  fontSize: "0.9rem",
                }}
              >
                <span
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{
                    width: "28px",
                    height: "28px",
                    background: "rgba(253, 126, 20, 0.2)",
                    color: "#fd7e14",
                  }}
                >
                  <FileText size={14} />
                </span>
                <span className="text-dark">{alert.msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p
        className="text-center small mb-0 mt-3 py-2 rounded-2"
        style={{
          backgroundColor: propertiesSelectedCount > 0 ? "var(--app-primary-lighter, rgba(12, 44, 85, 0.08))" : "rgba(108, 117, 125, 0.12)",
          color: propertiesSelectedCount > 0 ? "var(--app-primary)" : "#6c757d",
          fontWeight: 500,
        }}
      >
        {propertiesSelectedCount > 0
          ? t("bulkProductUpdate.propertiesWillChange", { count: propertiesSelectedCount })
          : t("bulkProductUpdate.propertiesNoChange")}
      </p>
    </div>
  );
}

function SeoForm({ t, propertySelected, propertyValues, setPropertyChecked, setPropertyValue }) {
  const seoTitleInputRef = useRef(null);
  const seoDescInputRef = useRef(null);

  const insertSeoTag = (field, variableValue) => {
    const ref = field === "seoTitle" ? seoTitleInputRef : seoDescInputRef;
    const current = (field === "seoTitle" ? propertyValues.seoTitle : propertyValues.seoDescription) ?? "";
    const el = ref.current;
    const start = el ? el.selectionStart : current.length;
    const end = el ? el.selectionEnd : current.length;
    const insert = start < current.length && current[start - 1] !== " " && start > 0 ? " " + variableValue : variableValue;
    const next = current.slice(0, start) + insert + current.slice(end);
    setPropertyValue(field, next);
    if (el) {
      const newPos = start + insert.length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(newPos, newPos);
      });
    }
  };

  const summaryPill = (summary) =>
    summary != null ? (
      <span
        className="small flex-shrink-0 px-2 py-1 rounded-pill text-nowrap"
        style={{
          background: "linear-gradient(135deg, rgba(255, 193, 7, 0.35) 0%, rgba(253, 126, 20, 0.2) 100%)",
          color: "#92400e",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      >
        {t("bulkProductUpdate.willChangeTo", { value: summary })}
      </span>
    ) : null;

  const block = (key, label, placeholder, hint, value) => (
    <div
      key={key}
      className="d-flex gap-3 py-2 px-3 rounded-2 flex-wrap"
      style={{
        backgroundColor: propertySelected[key] ? "rgba(255, 243, 205, 0.35)" : "rgba(255,255,255,0.7)",
        marginBottom: "4px",
        borderLeft: propertySelected[key] ? "3px solid rgba(253, 126, 20, 0.5)" : undefined,
        alignItems: "flex-start",
      }}
    >
      <input
        type="checkbox"
        id={`step2-seo-${key}`}
        className="form-check-input flex-shrink-0 mt-0"
        style={{ marginTop: "0.35rem" }}
        checked={!!propertySelected[key]}
        onChange={(e) => setPropertyChecked(key, e.target.checked)}
        aria-label={label}
      />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap w-100">
          <label
            htmlFor={`step2-seo-${key}`}
            className="text-nowrap flex-shrink-0 small text-dark mb-0"
            style={{ cursor: "pointer" }}
          >
            {label}
          </label>
          <input
            ref={key === "seoTitle" ? seoTitleInputRef : seoDescInputRef}
            type="text"
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: "120px" }}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setPropertyValue(key, e.target.value)}
            disabled={!propertySelected[key]}
          />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm flex-shrink-0"
            onClick={() => setPropertyValue(key, key === "seoTitle" ? "{product_name}" : "{description}")}
            disabled={!propertySelected[key]}
          >
            {t("bulkProductUpdate.clear")}
          </button>
        </div>
        {hint && <span className="small text-muted d-block mt-1">{hint}</span>}
        <div className="d-flex flex-wrap gap-1 mt-1">
          {SEO_TAG_VARIABLES.map((v) => (
            <button
              key={v.value}
              type="button"
              className="btn btn-outline-secondary btn-sm py-0 px-1"
              style={{ fontSize: "0.75rem" }}
              onClick={() => insertSeoTag(key, v.value)}
              disabled={!propertySelected[key]}
            >
              + {t("bulkProductUpdate." + v.labelKey)}
            </button>
          ))}
        </div>
        {propertySelected[key] && (
          <div className="mt-1">
            {summaryPill(value?.trim() || "—")}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="seo-form">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.seoKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted small" style={{ fontSize: "0.85rem" }}>
          <li>{t("bulkProductUpdate.seoKnowFirstBullet1")}</li>
          <li>{t("bulkProductUpdate.seoKnowFirstBullet2")}</li>
        </ul>
      </div>
      <div className="rounded-3 overflow-hidden" style={{ border: "1px solid var(--bs-border-color)" }}>
        {block(
          "seoTitle",
          t("bulkProductUpdate.propSeoTitle"),
          t("bulkProductUpdate.propSeoTitlePlaceholder"),
          t("bulkProductUpdate.propSeoTitleHint"),
          propertyValues.seoTitle
        )}
        {block(
          "seoDescription",
          t("bulkProductUpdate.propSeoDescription"),
          t("bulkProductUpdate.propSeoDescPlaceholder"),
          t("bulkProductUpdate.propSeoDescHint"),
          propertyValues.seoDescription
        )}
      </div>
    </div>
  );
}

// Replace Text: API field keys and operation ids
const REPLACE_TEXT_FIELD_OPTIONS = [
  { value: "name", labelKey: "replaceTextFieldName" },
  { value: "description", labelKey: "replaceTextFieldDescription" },
  { value: "shortDescription", labelKey: "replaceTextFieldSubtitle" },
  { value: "seoTitle", labelKey: "replaceTextFieldSeoTitle" },
  { value: "seoDescription", labelKey: "replaceTextFieldSeoDescription" },
];
const REPLACE_TEXT_OPERATION_OPTIONS = [
  { value: "find_replace", labelKey: "replaceTextOpFindReplace" },
  { value: "replace_entire", labelKey: "replaceTextOpReplaceEntire" },
  { value: "suffix", labelKey: "replaceTextOpSuffix" },
  { value: "prefix", labelKey: "replaceTextOpPrefix" },
  { value: "erase", labelKey: "replaceTextOpErase" },
];

function applyReplaceRule(currentValue, rule) {
  const str = String(currentValue ?? "");
  const find = String(rule.findText ?? "");
  const replace = String(rule.replaceText ?? "");
  switch (rule.operation) {
    case "find_replace":
      return find === "" ? str : str.split(find).join(replace);
    case "replace_entire":
      return replace;
    case "suffix":
      return str + replace;
    case "prefix":
      return replace + str;
    case "erase":
      return "";
    default:
      return str;
  }
}

function ReplaceTextForm({ t, rules, setRules }) {
  const addRule = () => {
    setRules((prev) => [
      ...prev,
      {
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        field: "shortDescription",
        operation: "find_replace",
        findText: "",
        replaceText: "",
      },
    ]);
  };
  const clearRules = () => setRules([]);
  const updateRule = (id, patch) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRule = (id) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="replace-text-form">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.replaceTextKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted small" style={{ fontSize: "0.85rem" }}>
          <li>{t("bulkProductUpdate.replaceTextKnowFirstBullet1")}</li>
          <li>{t("bulkProductUpdate.replaceTextKnowFirstBullet2")}</li>
        </ul>
      </div>
      <div className="rounded-3 overflow-hidden" style={{ border: "1px solid var(--bs-border-color)" }}>
        <div className="p-3" style={{ backgroundColor: "rgba(248, 249, 250, 0.8)" }}>
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="mb-4 p-3 rounded-2 border"
              style={{ backgroundColor: "rgba(255,255,255,0.9)", borderColor: "var(--bs-border-color)" }}
            >
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fw-semibold small text-dark">{t("bulkProductUpdate.replaceTextChangeField")}</span>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: "160px" }}
                  value={rule.field}
                  onChange={(e) => updateRule(rule.id, { field: e.target.value })}
                >
                  {REPLACE_TEXT_FIELD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t("bulkProductUpdate." + opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fw-semibold small text-dark">{t("bulkProductUpdate.replaceTextOperation")}</span>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: "200px" }}
                  value={rule.operation}
                  onChange={(e) => updateRule(rule.id, { operation: e.target.value })}
                >
                  {REPLACE_TEXT_OPERATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t("bulkProductUpdate." + opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              {rule.operation === "find_replace" && (
                <>
                  <div className="mb-2">
                    <label className="fw-semibold small text-dark d-block mb-1">{t("bulkProductUpdate.replaceTextFindLabel")}</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder={t("bulkProductUpdate.replaceTextFindPlaceholder")}
                      value={rule.findText}
                      onChange={(e) => updateRule(rule.id, { findText: e.target.value })}
                      style={{ resize: "vertical", minHeight: "60px" }}
                    />
                    <div className="small text-muted text-end">{t("bulkProductUpdate.replaceTextChars", { count: (rule.findText ?? "").length })}</div>
                  </div>
                  <div className="mb-2">
                    <label className="fw-semibold small text-dark d-block mb-1">{t("bulkProductUpdate.replaceTextReplaceLabel")}</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder={t("bulkProductUpdate.replaceTextReplacePlaceholder")}
                      value={rule.replaceText}
                      onChange={(e) => updateRule(rule.id, { replaceText: e.target.value })}
                      style={{ resize: "vertical", minHeight: "60px" }}
                    />
                    <div className="small text-muted text-end">{t("bulkProductUpdate.replaceTextChars", { count: (rule.replaceText ?? "").length })}</div>
                  </div>
                </>
              )}
              {(rule.operation === "replace_entire" || rule.operation === "suffix" || rule.operation === "prefix") && (
                <div className="mb-2">
                  <label className="fw-semibold small text-dark d-block mb-1">{t("bulkProductUpdate.replaceTextReplaceLabel")}</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder={t("bulkProductUpdate.replaceTextReplacePlaceholder")}
                    value={rule.replaceText}
                    onChange={(e) => updateRule(rule.id, { replaceText: e.target.value })}
                    style={{ resize: "vertical", minHeight: "60px" }}
                  />
                  <div className="small text-muted text-end">{t("bulkProductUpdate.replaceTextChars", { count: (rule.replaceText ?? "").length })}</div>
                </div>
              )}
              {rule.operation === "erase" && (
                <p className="small text-muted mb-0">{t("bulkProductUpdate.replaceTextOpErase")} – field will be cleared.</p>
              )}
              <div className="mt-2">
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRule(rule.id)}>
                  {t("bulkProductUpdate.clear")}
                </button>
              </div>
            </div>
          ))}
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-primary btn-sm" onClick={addRule}>
              <Plus size={16} className="me-1" style={{ verticalAlign: "middle" }} />
              {t("bulkProductUpdate.replaceTextAddRule")}
            </button>
            {rules.length > 0 && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearRules}>
                {t("bulkProductUpdate.replaceTextClearRules")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShippingForm({
  t,
  shippingConfig,
  setShippingConfig,
  shippingStoreFixedFee,
  setShippingStoreFixedFee,
  shippingStoreFixedFeeAmount,
  setShippingStoreFixedFeeAmount,
  shippingSpecifyMode,
  setShippingSpecifyMode,
  shippingOptions,
  shippingSelectedMethodIds,
  setShippingSelectedMethodIds,
  shippingFixedRateAmount,
  setShippingFixedRateAmount,
}) {
  const toggleShippingMethod = (id) => {
    setShippingSelectedMethodIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const optionCardStyle = {
    backgroundColor: "rgba(12, 44, 85, 0.06)",
    border: "1px solid rgba(12, 44, 85, 0.2)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "0.5rem",
  };
  const alertBoxStyle = {
    backgroundColor: "rgba(220, 53, 69, 0.08)",
    border: "1px solid rgba(220, 53, 69, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  };

  const selectOption = (value) => {
    setShippingConfig((prev) => (prev === value ? "" : value));
  };

  return (
    <div className="shipping-form">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.shippingKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted">
          <li>{t("bulkProductUpdate.shippingOneOnly")}</li>
        </ul>
      </div>

      {/* 1. Use store's shipping methods */}
      <div style={optionCardStyle}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="shipping-use-store"
              checked={shippingConfig === "store"}
              onChange={() => selectOption("store")}
            />
            <label className="form-check-label" htmlFor="shipping-use-store" style={{ cursor: "pointer" }}>
              {t("bulkProductUpdate.shippingUseStore")}
            </label>
          </div>
          {shippingConfig === "store" && (
            <>
              <div className="form-check form-switch mb-0 ms-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shipping-store-fee"
                  checked={shippingStoreFixedFee}
                  onChange={(e) => setShippingStoreFixedFee(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="shipping-store-fee" style={{ cursor: "pointer" }}>
                  {t("bulkProductUpdate.shippingAddFixedFee")}
                </label>
              </div>
              {shippingStoreFixedFee && (
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-sm"
                  style={{ width: "80px" }}
                  placeholder="0.00"
                  value={shippingStoreFixedFeeAmount}
                  onChange={(e) =>
                    setShippingStoreFixedFeeAmount(max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))
                  }
                />
              )}
            </>
          )}
        </div>
        {shippingConfig === "store" && (
          <div style={{ ...alertBoxStyle, marginTop: "0.75rem", marginBottom: 0 }}>
            {t("bulkProductUpdate.shippingAlertStore")}
          </div>
        )}
      </div>

      {/* 2. Specify shipping methods for this product */}
      <div style={optionCardStyle}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="shipping-specify"
            checked={shippingConfig === "specify"}
            onChange={() => selectOption("specify")}
          />
          <label className="form-check-label" htmlFor="shipping-specify" style={{ cursor: "pointer" }}>
            {t("bulkProductUpdate.shippingSpecifyMethods")}
          </label>
        </div>
        {shippingConfig === "specify" && (
          <>
            <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={shippingSpecifyMode}
                onChange={(e) => setShippingSpecifyMode(e.target.value)}
              >
                <option value="allow">{t("bulkProductUpdate.shippingAllow")}</option>
                <option value="disallow">{t("bulkProductUpdate.shippingDisallow")}</option>
              </select>
              <span className="small text-muted">{t("bulkProductUpdate.shippingAllowOnly")}</span>
            </div>
            {shippingOptions.length > 0 ? (
              <div className="mt-2 ps-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {shippingOptions
                  .filter((o) => o.enabled !== false)
                  .map((o) => (
                    <div key={o.id || o.title} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`shipping-method-${o.id}`}
                        checked={shippingSelectedMethodIds.includes(String(o.id))}
                        onChange={() => toggleShippingMethod(String(o.id))}
                      />
                      <label className="form-check-label small" htmlFor={`shipping-method-${o.id}`} style={{ cursor: "pointer" }}>
                        {o.title || o.id || t("bulkProductUpdate.shippingMethodUnnamed")}
                      </label>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="small text-muted mt-2 mb-0">{t("bulkProductUpdate.shippingNoMethods")}</p>
            )}
            <div style={{ ...alertBoxStyle, marginTop: "0.75rem", marginBottom: 0 }}>
              {t("bulkProductUpdate.shippingAlertSpecify")}
            </div>
          </>
        )}
      </div>

      {/* 3. Fixed rate per item (standalone) */}
      <div style={optionCardStyle}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="shipping-fixed-rate"
              checked={shippingConfig === "fixedRate"}
              onChange={() => selectOption("fixedRate")}
            />
            <label className="form-check-label" htmlFor="shipping-fixed-rate" style={{ cursor: "pointer" }}>
              {t("bulkProductUpdate.shippingFixedRatePerItem")}
            </label>
          </div>
          {shippingConfig === "fixedRate" && (
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm"
              style={{ width: "80px" }}
              placeholder="0.00"
              value={shippingFixedRateAmount}
              onChange={(e) =>
                setShippingFixedRateAmount(max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))
              }
            />
          )}
        </div>
        {shippingConfig === "fixedRate" && (
          <div style={{ ...alertBoxStyle, marginTop: "0.75rem", marginBottom: 0 }}>
            {t("bulkProductUpdate.shippingAlertFixedRate")}
          </div>
        )}
      </div>

      {/* 4. Free Shipping */}
      <div style={optionCardStyle}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="shipping-free"
            checked={shippingConfig === "free"}
            onChange={() => selectOption("free")}
          />
          <label className="form-check-label" htmlFor="shipping-free" style={{ cursor: "pointer" }}>
            {t("bulkProductUpdate.shippingFreeShipping")}
          </label>
        </div>
        {shippingConfig === "free" && (
          <div style={{ ...alertBoxStyle, marginTop: "0.75rem", marginBottom: 0 }}>
            {t("bulkProductUpdate.shippingAlertFree")}
          </div>
        )}
      </div>
    </div>
  );
}

function PricingForm({
  t,
  modificationDetailsOn,
  setModificationDetailsOn,
  roundingMode,
  setRoundingMode,
  roundingResolution,
  setRoundingResolution,
  pricingRule,
  setPricingRule,
  saveOldPriceToCompareAt,
  setSaveOldPriceToCompareAt,
  revertSalesPrice,
  setRevertSalesPrice,
  wholesaleRules,
  setWholesaleRules,
  clearWholesale,
  setClearWholesale,
}) {
  const alertBoxStyle = {
    backgroundColor: "rgba(220, 53, 69, 0.08)",
    border: "1px solid rgba(220, 53, 69, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  };

  const cardStyle = {
    backgroundColor: "rgba(12, 44, 85, 0.06)",
    border: "1px solid rgba(12, 44, 85, 0.2)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "0.5rem",
  };

  const updatePricingRule = (field, value) => {
    setPricingRule((prev) => ({ ...prev, [field]: value }));
  };
  const clearPricingRule = () => {
    setPricingRule({ type: "%", sign: "+", value: "0" });
  };

  const pricingRuleValue = parseFloat(pricingRule.value) || 0;
  const hasActiveRule = pricingRuleValue !== 0;

  const addWholesaleRule = () => {
    setWholesaleRules((prev) => [
      ...prev,
      {
        id: `wh-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        quantity: "2",
        price: "",
        type: "$",
      },
    ]);
  };
  const removeWholesaleRule = (id) => {
    setWholesaleRules((prev) => prev.filter((rule) => rule.id !== id));
  };
  const updateWholesaleRule = (id, field, value) => {
    setWholesaleRules((prev) => prev.map((rule) => (rule.id === id ? { ...rule, [field]: value } : rule)));
  };

  return (
    <div className="pricing-form">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h6 className="fw-bold mb-0 text-dark">{t("bulkProductUpdate.pricingTitle")}</h6>
      </div>

      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.pricingKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted small" style={{ fontSize: "0.85rem" }}>
          <li>{t("bulkProductUpdate.pricingKnow1")}</li>
          <li>{t("bulkProductUpdate.pricingKnow2")}</li>
          <li>{t("bulkProductUpdate.pricingKnow3")}</li>
          <li>{t("bulkProductUpdate.pricingKnow4")}</li>
          <li>{t("bulkProductUpdate.pricingKnow5")}</li>
          <li>{t("bulkProductUpdate.pricingKnow6")}</li>
          <li>{t("bulkProductUpdate.pricingKnow7")}</li>
        </ul>
      </div>

      {/* Modification details */}
      <div style={cardStyle}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="pricing-modification-details"
              checked={modificationDetailsOn}
              onChange={(e) => setModificationDetailsOn(e.target.checked)}
            />
            <label className="form-check-label fw-semibold" htmlFor="pricing-modification-details" style={{ cursor: "pointer" }}>
              {t("bulkProductUpdate.pricingModificationDetails")}
            </label>
          </div>
          {modificationDetailsOn && (
            <>
              <span className="small text-muted ms-2">{t("bulkProductUpdate.pricingSpecialRounding")}</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={roundingMode}
                onChange={(e) => setRoundingMode(e.target.value)}
              >
                <option value="nearest">{t("bulkProductUpdate.pricingRoundingNearest")}</option>
                <option value="up">{t("bulkProductUpdate.pricingRoundingUp")}</option>
                <option value="down">{t("bulkProductUpdate.pricingRoundingDown")}</option>
              </select>
              <div className="d-flex align-items-center gap-1">
                <span className="small text-muted">{t("bulkProductUpdate.pricingResolution")}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-sm"
                  style={{ width: "70px" }}
                  value={roundingResolution}
                  onChange={(e) => setRoundingResolution(max2DecimalsInput(onlyNumericInput(e.target.value)))}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Change price (single rule) */}
      <div style={cardStyle}>
        <p className="mb-2 small fw-semibold text-dark">{t("bulkProductUpdate.pricingChangeBy")}</p>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={pricingRule.type}
            onChange={(e) => updatePricingRule("type", e.target.value)}
          >
            <option value="%">%</option>
            <option value="absolute">{t("bulkProductUpdate.pricingAbsolute")}</option>
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={pricingRule.sign}
            onChange={(e) => updatePricingRule("sign", e.target.value)}
          >
            <option value="+">+</option>
            <option value="-">-</option>
          </select>
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            style={{ width: "80px" }}
            placeholder="0.00"
            value={pricingRule.value}
            onChange={(e) => updatePricingRule("value", max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value))))}
          />
          <span className="small text-muted">{t("bulkProductUpdate.pricingAnd")}</span>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="pricing-save-compare-at"
              checked={saveOldPriceToCompareAt}
              onChange={(e) => setSaveOldPriceToCompareAt(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="pricing-save-compare-at" style={{ cursor: "pointer" }}>
              {saveOldPriceToCompareAt ? t("bulkProductUpdate.pricingSaveCompareAt") : t("bulkProductUpdate.pricingDontSaveCompareAt")}
            </label>
          </div>
          <span className="small text-muted">{t("bulkProductUpdate.pricingOldPriceToCompareAt")}</span>
          {hasActiveRule && (
            <button type="button" className="btn btn-sm btn-outline-secondary ms-1" onClick={clearPricingRule}>
              {t("bulkProductUpdate.pricingClearRule")}
            </button>
          )}
        </div>
        {hasActiveRule && (
          <div
            className="mt-2 p-2 rounded small"
            style={{
              background: "linear-gradient(135deg, rgba(255, 243, 205, 0.5) 0%, rgba(255, 237, 213, 0.5) 100%)",
              border: "1px solid rgba(253, 126, 20, 0.25)",
            }}
          >
            <p className="mb-0 fw-semibold text-dark">{t("bulkProductUpdate.pricingChangeSummaryTitle")}</p>
            <p className="mb-0 text-muted">
              {pricingRule.type === "%"
                ? t("bulkProductUpdate.pricingChangeSummaryPercent", {
                    direction: pricingRule.sign === "+" ? t("bulkProductUpdate.pricingDirectionIncrease") : t("bulkProductUpdate.pricingDirectionDecrease"),
                    value: pricingRuleValue,
                  })
                : t("bulkProductUpdate.pricingChangeSummaryAbsolute", {
                    direction: pricingRule.sign === "+" ? t("bulkProductUpdate.pricingDirectionIncrease") : t("bulkProductUpdate.pricingDirectionDecrease"),
                    value: pricingRule.value || "0",
                  })}
            </p>
            {saveOldPriceToCompareAt && (
              <p className="mb-0 text-muted mt-1">{t("bulkProductUpdate.pricingChangeSummarySaveCompareAt")}</p>
            )}
          </div>
        )}
      </div>

      {/* Wholesale / bulk prices */}
      <div style={cardStyle}>
        <p className="mb-2 small fw-semibold text-dark">{t("bulkProductUpdate.pricingWholesaleTitle")}</p>
        <div className="d-flex flex-column gap-2">
          {wholesaleRules.map((rule) => (
            <div
              key={rule.id}
              className="p-3 rounded"
              style={{
                background: "linear-gradient(135deg, rgba(255, 243, 205, 0.5) 0%, rgba(255, 237, 213, 0.5) 100%)",
                border: "1px solid rgba(253, 126, 20, 0.25)",
              }}
            >
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="small text-dark">{t("bulkProductUpdate.pricingWholesaleWhenBuying")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control form-control-sm"
                  style={{ width: "60px" }}
                  placeholder="0"
                  value={rule.quantity}
                  onChange={(e) =>
                    updateWholesaleRule(
                      rule.id,
                      "quantity",
                      clampNonNegative(integerOnlyInput(onlyNumericInput(e.target.value)))
                    )
                  }
                />
                <span className="small text-dark">{t("bulkProductUpdate.pricingWholesaleUnitsReducedTo")}</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "60px" }}
                  value={rule.type || "$"}
                  onChange={(e) => updateWholesaleRule(rule.id, "type", e.target.value)}
                >
                  <option value="$">$</option>
                  <option value="%">%</option>
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-sm"
                  style={{ width: "80px" }}
                  placeholder={rule.type === "%" ? "0" : "0.00"}
                  value={rule.price}
                  onChange={(e) =>
                    updateWholesaleRule(
                      rule.id,
                      "price",
                      rule.type === "%"
                        ? clampNonNegative(onlyNumericInput(e.target.value))
                        : max2DecimalsInput(clampNonNegative(onlyNumericInput(e.target.value)))
                    )
                  }
                />
                {rule.type === "%" && <span className="text-muted small">%</span>}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => removeWholesaleRule(rule.id)}
                >
                  {t("bulkProductUpdate.pricingWholesaleRemove")}
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-primary align-self-start" onClick={addWholesaleRule}>
            + {t("bulkProductUpdate.pricingWholesaleAddRule")}
          </button>
          {wholesaleRules.length > 0 && (
            <div
              className="mt-1 p-2 rounded small"
              style={{
                background: "linear-gradient(135deg, rgba(255, 243, 205, 0.5) 0%, rgba(255, 237, 213, 0.5) 100%)",
                border: "1px solid rgba(253, 126, 20, 0.25)",
              }}
            >
              <p className="mb-1 fw-semibold text-dark">{t("bulkProductUpdate.pricingWholesaleSummaryTitle")}</p>
              {wholesaleRules.map((rule) => (
                <p key={rule.id} className="mb-0 text-muted">
                  {t("bulkProductUpdate.pricingWholesaleSummaryText", {
                    quantity: rule.quantity || "0",
                    amount: rule.type === "%" ? `${rule.price || "0"} %` : `${rule.price || "0.00"} $`,
                  })}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="form-check form-switch mb-0 mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="pricing-clear-wholesale"
            checked={clearWholesale}
            onChange={(e) => setClearWholesale(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="pricing-clear-wholesale" style={{ cursor: "pointer" }}>
            {t("bulkProductUpdate.pricingWholesaleClearAll")}
          </label>
        </div>
      </div>

      {/* Revert sales price */}
      <div style={cardStyle}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="pricing-revert"
            checked={revertSalesPrice}
            onChange={(e) => setRevertSalesPrice(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="pricing-revert" style={{ cursor: "pointer" }}>
            {t("bulkProductUpdate.pricingRevertSalesPrice")}
          </label>
        </div>
      </div>
    </div>
  );
}

function AttributesForm({
  t,
  attributeRules,
  setAttributeRules,
  attributesImportModalOpen,
  setAttributesImportModalOpen,
  attributesSearchKeyword,
  setAttributesSearchKeyword,
  attributesSearchResults,
  setAttributesSearchResults,
  attributesSearchLoading,
  setAttributesSearchLoading,
  attributesSelectedProductId,
  setAttributesSelectedProductId,
  attributesSearchAttempted,
  setAttributesSearchAttempted,
  apiUrl,
  authData,
}) {
  const alertBoxStyle = {
    backgroundColor: "rgba(220, 53, 69, 0.08)",
    border: "1px solid rgba(220, 53, 69, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  };

  const removeRule = (id) => {
    setAttributeRules((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRule = (id, field, value) => {
    setAttributeRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const clearAllRules = () => {
    setAttributeRules([]);
  };

  const searchProductsForImport = async () => {
    if (!authData?.store_id || !authData?.access_token) return;
    setAttributesSearchAttempted(true);
    setAttributesSearchLoading(true);
    setAttributesSearchResults([]);
    setAttributesSelectedProductId(null);
    try {
      const url = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&limit=20&offset=0&keyword=${encodeURIComponent(attributesSearchKeyword.trim())}`;
      const res = await fetch(url);
      const data = await res.json();
      if (res.ok && Array.isArray(data.items)) {
        setAttributesSearchResults(data.items);
        if (data.items.length > 0) setAttributesSelectedProductId(data.items[0].id);
      }
    } catch (e) {
      logger.error("AttributesForm searchProductsForImport", e);
    } finally {
      setAttributesSearchLoading(false);
    }
  };

  // Ecwid Get product: attributes[] has id, name, nameTranslated, value, valueTranslated, type, show
  const getAttrDisplay = (field, translated) => {
    if (field != null && field !== "") return typeof field === "string" ? field : String(field);
    if (translated && typeof translated === "object") {
      const vals = Object.values(translated).filter((v) => v != null && v !== "");
      if (vals.length) return String(vals[0]);
    }
    return "";
  };

  // Fallback label from Ecwid attribute type (UPC, BRAND, COLOR, etc.) when name is empty
  const getAttrName = (a) => {
    const name = getAttrDisplay(a.name, a.nameTranslated);
    if (name.trim() !== "") return name;
    const t = (a.type || "").toUpperCase();
    if (t === "UPC") return "UPC";
    if (t === "BRAND") return "Brand";
    if (t === "COLOR") return "Color";
    if (t === "SIZE") return "Size";
    if (t === "GENDER") return "Gender";
    if (t === "AGE_GROUP") return "Age group";
    if (t === "CUSTOM") return "Custom";
    if (t === "PRICE_PER_UNIT") return "Price per unit";
    if (t === "UNITS_IN_PRODUCT") return "Units in product";
    return t ? t.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "";
  };

  const importFromProduct = async () => {
    if (!attributesSelectedProductId || !authData?.store_id || !authData?.access_token) return;
    try {
      const url = `${apiUrl}/api/products/${attributesSelectedProductId}?storeId=${authData.store_id}&token=${authData.access_token}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) {
        logger.error("AttributesForm importFromProduct res not ok", res.status, data);
        return;
      }
      // Ecwid: product.attributes is array of { id, name, nameTranslated, value, valueTranslated, type, show }
      const rawAttrs = data.attributes;
      let attrs = Array.isArray(rawAttrs) ? rawAttrs : [];
      let newRules = attrs
        .map((a) => ({
          id: `attr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: getAttrName(a),
          value: getAttrDisplay(a.value, a.valueTranslated),
        }))
        .filter((r) => r.name.trim() !== "");

      // If product has no attribute values, use product type (class) to get attribute definitions with empty values
      if (newRules.length === 0) {
        const classId = data.productClassId ?? 0;
        const classUrl = `${apiUrl}/api/classes/${classId}?storeId=${authData.store_id}&token=${authData.access_token}`;
        const classRes = await fetch(classUrl);
        const classData = await classRes.json();
        if (classRes.ok && classData) {
          // Get product type returns object { id, name, attributes } or array [{ id, attributes }]
          const classObj = Array.isArray(classData) ? classData[0] : classData;
          const classAttrs = Array.isArray(classObj?.attributes) ? classObj.attributes : [];
          newRules = classAttrs
            .map((a) => ({
              id: `attr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              name: getAttrName(a),
              value: "",
            }))
            .filter((r) => r.name.trim() !== "");
        }
      }

      setAttributeRules((prev) => [...prev, ...newRules]);
      setAttributesImportModalOpen(false);
      setAttributesSearchKeyword("");
      setAttributesSearchResults([]);
      setAttributesSelectedProductId(null);
    } catch (e) {
      logger.error("AttributesForm importFromProduct", e);
    }
  };

  const closeImportModal = () => {
    setAttributesImportModalOpen(false);
    setAttributesSearchKeyword("");
    setAttributesSearchResults([]);
    setAttributesSelectedProductId(null);
    setAttributesSearchAttempted(false);
  };

  return (
    <div className="attributes-form">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.attributesKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted">
          <li>{t("bulkProductUpdate.attributesNotCaseSensitive")}</li>
          <li>{t("bulkProductUpdate.attributesEmptyValueEmpties")}</li>
        </ul>
      </div>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <span className="fw-semibold">{t("bulkProductUpdate.attributesEditValues")}</span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setAttributesImportModalOpen(true)}
          >
            {t("bulkProductUpdate.attributesImportFromProduct")}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={clearAllRules}
            disabled={attributeRules.length === 0}
          >
            {t("bulkProductUpdate.attributesClearChanges")}
          </button>
        </div>
      </div>

      {attributeRules.length === 0 ? (
        <p className="small text-muted mb-0">
          {t("bulkProductUpdate.attributesNoRulesYet")}
        </p>
      ) : (
        <div className="d-flex flex-column gap-2">
          {attributeRules.map((rule) => (
            <div
              key={rule.id}
              className="d-flex align-items-center gap-2 flex-wrap py-2 px-3 rounded-2"
              style={{ backgroundColor: "rgba(255, 243, 205, 0.35)" }}
            >
              <span className="small text-muted flex-shrink-0">
                {t("bulkProductUpdate.attributesSetNamedBefore")}
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: "100px" }}
                placeholder="name"
                value={rule.name}
                onChange={(e) => updateRule(rule.id, "name", e.target.value)}
              />
              <span className="small text-muted flex-shrink-0">
                {t("bulkProductUpdate.attributesSetNamedToValue")}
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: "200px" }}
                placeholder="value"
                value={rule.value}
                onChange={(e) => updateRule(rule.id, "value", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm p-1"
                aria-label="Remove"
                onClick={() => removeRule(rule.id)}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {attributesImportModalOpen && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeImportModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("bulkProductUpdate.attributesImportModalTitle")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeImportModal}
                />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-2 mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("bulkProductUpdate.attributesImportSearchPlaceholder")}
                    value={attributesSearchKeyword}
                    onChange={(e) => setAttributesSearchKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchProductsForImport()}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={searchProductsForImport}
                    disabled={attributesSearchLoading}
                  >
                    {attributesSearchLoading ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      t("bulkProductUpdate.attributesImportGo")
                    )}
                  </button>
                </div>
                {attributesSearchAttempted && attributesSearchResults.length === 0 && !attributesSearchLoading && (
                  <p className="small text-muted mb-0">{t("bulkProductUpdate.attributesNoResults")}</p>
                )}
                <div className="list-group list-group-flush" style={{ maxHeight: "280px", overflowY: "auto" }}>
                  {attributesSearchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${attributesSelectedProductId === p.id ? "active" : ""}`}
                      onClick={() => setAttributesSelectedProductId(p.id)}
                    >
                      <span>
                        {p.name || p.id}
                        {p.sku ? ` (${p.sku})` : ""}
                      </span>
                      {attributesSelectedProductId === p.id && <Plus size={18} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeImportModal}>
                  {t("bulkProductUpdate.attributesImportCancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={importFromProduct}
                  disabled={!attributesSelectedProductId || attributesSearchLoading}
                >
                  {t("bulkProductUpdate.attributesImportSelect")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoriesForm({
  t,
  categories,
  categoriesLoading,
  categoryIdsToAssign,
  setCategoryIdsToAssign,
  categoryAction,
  setCategoryAction,
  categoryPickerModalOpen,
  setCategoryPickerModalOpen,
}) {
  const [modalCheckedIds, setModalCheckedIds] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");

  useEffect(() => {
    if (categoryPickerModalOpen) {
      setModalCheckedIds([...categoryIdsToAssign]);
      setCategorySearch("");
    }
  }, [categoryPickerModalOpen, categoryIdsToAssign]);

  const confirmCategorySelection = () => {
    setCategoryIdsToAssign([...modalCheckedIds]);
    setCategoryPickerModalOpen(false);
  };

  const toggleCategory = (id) => {
    setModalCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredCategories = categories.filter(
    (c) =>
      !categorySearch.trim() ||
      (c.name || "").toLowerCase().includes(categorySearch.trim().toLowerCase())
  );

  const alertBoxStyle = {
    backgroundColor: "rgba(220, 53, 69, 0.08)",
    border: "1px solid rgba(220, 53, 69, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  };

  const selectedNames = categoryIdsToAssign
    .map((id) => categories.find((c) => c.id === id)?.name)
    .filter(Boolean);

  return (
    <div className="categories-form">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.categoriesKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted">
          <li>{t("bulkProductUpdate.categoriesKnowFirstBullet1")}</li>
          <li>{t("bulkProductUpdate.categoriesKnowFirstBullet2")}</li>
        </ul>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span className="fw-semibold">{t("bulkProductUpdate.categoriesIWantTo")}</span>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto", minWidth: "100px" }}
          value={categoryAction}
          onChange={(e) => setCategoryAction(e.target.value)}
        >
          <option value="add">{t("bulkProductUpdate.categoriesActionAdd")}</option>
          <option value="remove">{t("bulkProductUpdate.categoriesActionRemove")}</option>
        </select>
        <span className="text-muted">
          {categoryAction === "add"
            ? t("bulkProductUpdate.categoriesToSuffix")
            : t("bulkProductUpdate.categoriesFromSuffix")}
        </span>
        <div className="d-flex gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setCategoryPickerModalOpen(true)}
          >
            {t("bulkProductUpdate.categoriesPickCategories")}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setCategoryIdsToAssign([])}
            disabled={categoryIdsToAssign.length === 0}
          >
            {t("bulkProductUpdate.categoriesClearRules")}
          </button>
        </div>
      </div>

      {categoryIdsToAssign.length === 0 ? (
        <p className="small text-muted mb-0">{t("bulkProductUpdate.categoriesNoneSelected")}</p>
      ) : (
        <p className="small mb-0">
          {selectedNames.join(", ")}
        </p>
      )}

      {categoryPickerModalOpen && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setCategoryPickerModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("bulkProductUpdate.categoriesModalTitle")}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setCategoryPickerModalOpen(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("bulkProductUpdate.categoriesSearchPlaceholder")}
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                  />
                </div>
                <div className="list-group list-group-flush" style={{ maxHeight: "280px", overflowY: "auto" }}>
                  {categoriesLoading ? (
                    <p className="list-group-item text-muted mb-0">{t("mainApp.loading")}</p>
                  ) : (
                    filteredCategories.map((c) => (
                      <label
                        key={c.id}
                        className="list-group-item list-group-item-action d-flex align-items-center gap-2 mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={modalCheckedIds.includes(c.id)}
                          onChange={() => toggleCategory(c.id)}
                        />
                        <span>{c.name || c.id}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCategoryPickerModalOpen(false)}
                >
                  {t("bulkProductUpdate.categoriesModalCancel")}
                </button>
                <button type="button" className="btn btn-primary" onClick={confirmCategorySelection}>
                  {t("bulkProductUpdate.categoriesModalSelect")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ClearGalleryForm = ({ t, clearGalleryImages, setClearGalleryImages, clearGalleryFiles, setClearGalleryFiles }) => (
  <div className="d-flex flex-column gap-3">
    <div style={IMPORTANT_INFO_CARD_STYLE}>
      <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.clearGalleryKnowFirst")}</p>
      <ul className="mb-0 ps-3 text-muted small" style={{ fontSize: "0.85rem" }}>
        <li>{t("bulkProductUpdate.clearGalleryKnowFirstBullet1")}</li>
        <li>{t("bulkProductUpdate.clearGalleryKnowFirstBullet2")}</li>
      </ul>
    </div>
    <div className="p-3 rounded border bg-white d-flex flex-column gap-2">
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="clearGalleryImages"
          checked={clearGalleryImages}
          onChange={(e) => setClearGalleryImages(e.target.checked)}
        />
        <label className="form-check-label ms-2" htmlFor="clearGalleryImages">
          {t("bulkProductUpdate.clearGalleryDeleteImages")}
        </label>
      </div>
      {clearGalleryImages && (
        <div className="p-2 rounded border border-danger bg-danger bg-opacity-10">
          <strong className="text-danger">{t("bulkProductUpdate.clearGalleryWarningImages")}</strong>
        </div>
      )}
    </div>
    {/* <div className="p-3 rounded border bg-white d-flex flex-column gap-2">
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="clearGalleryFiles"
          checked={clearGalleryFiles}
          onChange={(e) => setClearGalleryFiles(e.target.checked)}
        />
        <label className="form-check-label ms-2" htmlFor="clearGalleryFiles">
          {t("bulkProductUpdate.clearGalleryDeleteFiles")}
        </label>
      </div>
      {clearGalleryFiles && (
        <div className="p-2 rounded border border-danger bg-danger bg-opacity-10">
          <strong className="text-danger">{t("bulkProductUpdate.clearGalleryWarningFiles")}</strong>
        </div>
      )}
    </div> */}
  </div>
);

function RelatedProductsForm({
  t,
  deleteAllRelatedProducts,
  setDeleteAllRelatedProducts,
  relatedProductsToAdd,
  setRelatedProductsToAdd,
  apiUrl,
  authData,
  productImageUrl,
}) {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [relatedSearchKeyword, setRelatedSearchKeyword] = useState("");
  const [relatedSearchResults, setRelatedSearchResults] = useState([]);
  const [relatedSearchLoading, setRelatedSearchLoading] = useState(false);
  const [relatedSearchAttempted, setRelatedSearchAttempted] = useState(false);
  const [relatedSelectedIds, setRelatedSelectedIds] = useState(new Set());

  const warningBoxStyle = {
    backgroundColor: "rgba(220, 53, 69, 0.08)",
    border: "1px solid rgba(220, 53, 69, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  };

  const searchRelatedProducts = async () => {
    if (!authData?.store_id || !authData?.access_token) return;
    setRelatedSearchAttempted(true);
    setRelatedSearchLoading(true);
    setRelatedSearchResults([]);
    setRelatedSelectedIds(new Set());
    try {
      const url = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&limit=50&offset=0&keyword=${encodeURIComponent(relatedSearchKeyword.trim())}`;
      const res = await fetch(url);
      const data = await res.json();
      if (res.ok && Array.isArray(data.items)) {
        setRelatedSearchResults(data.items);
      }
    } catch (e) {
      logger.error("RelatedProductsForm searchRelatedProducts", e);
    } finally {
      setRelatedSearchLoading(false);
    }
  };

  const toggleRelatedSelected = (id) => {
    setRelatedSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addSelectedToRelated = () => {
    const existingIds = new Set(relatedProductsToAdd.map((p) => p.id));
    const toAdd = relatedSearchResults
      .filter((p) => relatedSelectedIds.has(p.id) && !existingIds.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name || String(p.id),
        imageUrl: productImageUrl(p) || "",
      }));
    setRelatedProductsToAdd((prev) => [...prev, ...toAdd]);
    setImportModalOpen(false);
    setRelatedSearchKeyword("");
    setRelatedSearchResults([]);
    setRelatedSelectedIds(new Set());
    setRelatedSearchAttempted(false);
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setRelatedSearchKeyword("");
    setRelatedSearchResults([]);
    setRelatedSelectedIds(new Set());
    setRelatedSearchAttempted(false);
  };

  const removeFromRelated = (id) => {
    setRelatedProductsToAdd((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div style={IMPORTANT_INFO_CARD_STYLE}>
        <p className="fw-semibold text-dark mb-1">{t("bulkProductUpdate.relatedKnowFirst")}</p>
        <ul className="mb-0 ps-3 text-muted">
          <li>{t("bulkProductUpdate.relatedKnowFirst1")}</li>
          <li>{t("bulkProductUpdate.relatedKnowFirst2")}</li>
          <li>{t("bulkProductUpdate.relatedKnowFirst3")}</li>
        </ul>
      </div>

      <div className="p-3 rounded border bg-white" style={{ backgroundColor: "rgba(12, 44, 85, 0.06)" }}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="deleteAllRelatedProducts"
            checked={deleteAllRelatedProducts}
            onChange={(e) => setDeleteAllRelatedProducts(e.target.checked)}
          />
          <label className="form-check-label ms-2" htmlFor="deleteAllRelatedProducts">
            {t("bulkProductUpdate.relatedDeleteAll")}
          </label>
        </div>
      </div>

      <div className="rounded border overflow-hidden">
        <div className="p-2 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: "rgba(12, 44, 85, 0.08)" }}>
          <span className="fw-semibold small">{t("bulkProductUpdate.relatedProductsToAdd")}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setImportModalOpen(true)}>
              {t("bulkProductUpdate.relatedImportProducts")}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setRelatedProductsToAdd([])}
              disabled={relatedProductsToAdd.length === 0}
            >
              {t("bulkProductUpdate.relatedClearSelected")}
            </button>
          </div>
        </div>
        <div className="p-3 bg-white min-vh-0" style={{ minHeight: "120px" }}>
          {relatedProductsToAdd.length === 0 ? (
            <p className="small text-muted mb-0">{t("bulkProductUpdate.relatedNoProductsYet")}</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {relatedProductsToAdd.map((p) => (
                <div
                  key={p.id}
                  className="position-relative rounded border bg-light overflow-hidden"
                  style={{ width: "80px", height: "100px" }}
                >
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt="" className="w-100 h-100 object-fit-cover" style={{ objectFit: "cover" }} />
                  ) : (
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted small">
                      <ImageIcon size={24} />
                    </div>
                  )}
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 btn btn-sm p-0 bg-danger bg-opacity-75 text-white border-0 rounded-0"
                    style={{ fontSize: "0.65rem" }}
                    aria-label="Remove"
                    onClick={() => removeFromRelated(p.id)}
                  >
                    <Minus size={12} />
                  </button>
                  {p.name && (
                    <div className="position-absolute bottom-0 start-0 end-0 small text-truncate bg-dark bg-opacity-50 text-white px-1" style={{ fontSize: "0.65rem" }} title={p.name}>
                      {p.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-2 rounded border" style={{ backgroundColor: "rgba(255, 193, 7, 0.15)", borderColor: "rgba(253, 126, 20, 0.4)" }}>
        <p className="mb-0 small">{t("bulkProductUpdate.relatedBanner")}</p>
      </div>

      {importModalOpen && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeImportModal} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("bulkProductUpdate.relatedImportModalTitle")}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeImportModal} />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-2 mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("bulkProductUpdate.relatedImportSearchPlaceholder")}
                    value={relatedSearchKeyword}
                    onChange={(e) => setRelatedSearchKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchRelatedProducts()}
                  />
                  <button type="button" className="btn btn-primary" onClick={searchRelatedProducts} disabled={relatedSearchLoading}>
                    {relatedSearchLoading ? <span className="spinner-border spinner-border-sm" /> : t("bulkProductUpdate.relatedImportGo")}
                  </button>
                </div>
                {relatedSearchAttempted && relatedSearchResults.length === 0 && !relatedSearchLoading && (
                  <p className="small text-muted mb-0">{t("bulkProductUpdate.relatedNoResults")}</p>
                )}
                <div className="list-group list-group-flush" style={{ maxHeight: "320px", overflowY: "auto" }}>
                  {relatedSearchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${relatedSelectedIds.has(p.id) ? "active" : ""}`}
                      onClick={() => toggleRelatedSelected(p.id)}
                    >
                      <div className="flex-shrink-0 rounded overflow-hidden bg-light" style={{ width: "40px", height: "40px" }}>
                        {productImageUrl(p) ? (
                          <img src={productImageUrl(p)} alt="" className="w-100 h-100 object-fit-cover" />
                        ) : (
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                            <ImageIcon size={18} />
                          </div>
                        )}
                      </div>
                      <span className="flex-grow-1 text-start">{p.name || p.id}{p.sku ? ` (${p.sku})` : ""}</span>
                      {relatedSelectedIds.has(p.id) && <Plus size={18} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeImportModal}>
                  {t("bulkProductUpdate.relatedImportCancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addSelectedToRelated}
                  disabled={relatedSelectedIds.size === 0 || relatedSearchLoading}
                >
                  {t("bulkProductUpdate.relatedImportAddSelected")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STEP2_SECTIONS = [
  // { id: "images", labelKey: "bulkProductUpdate.sectionImages", icon: ImagePlus },
  { id: "properties", labelKey: "bulkProductUpdate.sectionProperties", icon: FileText },
  { id: "pricing", labelKey: "bulkProductUpdate.sectionPricing", icon: Tag },
  { id: "categories", labelKey: "bulkProductUpdate.sectionCategories", icon: FolderTree },
  { id: "clearGallery", labelKey: "bulkProductUpdate.sectionClearGallery", icon: Trash2 },
  { id: "attributes", labelKey: "bulkProductUpdate.sectionAttributes", icon: Sliders },
  { id: "seo", labelKey: "bulkProductUpdate.sectionSeo", icon: Layout },
  { id: "shipping", labelKey: "bulkProductUpdate.sectionShipping", icon: Truck },
  { id: "related", labelKey: "bulkProductUpdate.sectionRelated", icon: Link2 },
  { id: "replaceText", labelKey: "bulkProductUpdate.sectionReplaceText", icon: Type },
];

/** Section IDs that can appear in rollback history (for filter dropdown and table labels). */
const ROLLBACK_SECTION_IDS = ["properties", "seo", "replaceText", "related", "shipping", "pricing", "categories", "images"];

/** Shared style for "Important information" cards (light red) across all sections. */
const IMPORTANT_INFO_CARD_STYLE = {
  background: "linear-gradient(135deg, rgba(254, 226, 226, 0.6) 0%, rgba(254, 202, 202, 0.4) 100%)",
  border: "1px solid rgba(239, 68, 68, 0.25)",
  borderRadius: "12px",
  padding: "0.75rem 1rem",
  marginBottom: "1rem",
};

const SECTION_TAB_BULK_UPDATE = 'bulk-update';
const SECTION_TAB_ROLLBACK = 'rollback';

const BulkProductUpdate = () => {
  const { t } = useTranslation();
  const [sectionTab, setSectionTab] = useState(SECTION_TAB_BULK_UPDATE);
  const [step, setStep] = useState(1);
  const applyButtonRef = useRef(null);
  const revertButtonRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [keyword, setKeyword] = useState("");
  const [keywordApplied, setKeywordApplied] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedFilterType, setSelectedFilterType] = useState("keyword");
  const [filterInputValue, setFilterInputValue] = useState("");
  const [skuApplied, setSkuApplied] = useState("");
  const [priceFromApplied, setPriceFromApplied] = useState("");
  const [priceToApplied, setPriceToApplied] = useState("");
  const [enabledApplied, setEnabledApplied] = useState(null);
  const [inStockApplied, setInStockApplied] = useState(null);
  const [createdFromApplied, setCreatedFromApplied] = useState("");
  const [createdToApplied, setCreatedToApplied] = useState("");
  const [updatedFromApplied, setUpdatedFromApplied] = useState("");
  const [updatedToApplied, setUpdatedToApplied] = useState("");
  const [filterPriceToValue, setFilterPriceToValue] = useState("");
  const [attributeNameApplied, setAttributeNameApplied] = useState("");
  const [attributeValueApplied, setAttributeValueApplied] = useState("");
  const [filterAttributeValue, setFilterAttributeValue] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [step2OpenId, setStep2OpenId] = useState(null);
  const [propertySelected, setPropertySelected] = useState({});
  const [propertyValues, setPropertyValues] = useState({
    price: "",
    compareToPrice: "",
    weight: "",
    length: "",
    width: "",
    height: "",
    quantity: "",
    unlimited: false,
    stockWarning: "",
    enabled: true,
    shippingRequired: true,
    seoTitle: "{product_name}",
    seoDescription: "{description}",
    productType: "general",
  });
  const [shippingConfig, setShippingConfig] = useState("");
  const [shippingStoreFixedFee, setShippingStoreFixedFee] = useState(false);
  const [shippingStoreFixedFeeAmount, setShippingStoreFixedFeeAmount] = useState("");
  const [shippingSpecifyMode, setShippingSpecifyMode] = useState("allow");
  const [shippingSelectedMethodIds, setShippingSelectedMethodIds] = useState([]);
  const [shippingFixedRateAmount, setShippingFixedRateAmount] = useState("");
  const [shippingOptions, setShippingOptions] = useState([]);
  const [pricingModificationDetailsOn, setPricingModificationDetailsOn] = useState(true);
  const [pricingRoundingMode, setPricingRoundingMode] = useState("nearest");
  const [pricingRoundingResolution, setPricingRoundingResolution] = useState("0.01");
  const [pricingSaveOldPriceToCompareAt, setPricingSaveOldPriceToCompareAt] = useState(false);
  const [pricingRevertSalesPrice, setPricingRevertSalesPrice] = useState(false);
  const [pricingRule, setPricingRule] = useState({ type: "%", sign: "+", value: "0" });
  const [pricingWholesaleRules, setPricingWholesaleRules] = useState([]);
  const [pricingClearWholesale, setPricingClearWholesale] = useState(false);
  const [attributeRules, setAttributeRules] = useState([]);
  const [attributesImportModalOpen, setAttributesImportModalOpen] = useState(false);
  const [attributesSearchKeyword, setAttributesSearchKeyword] = useState("");
  const [attributesSearchResults, setAttributesSearchResults] = useState([]);
  const [attributesSearchLoading, setAttributesSearchLoading] = useState(false);
  const [attributesSelectedProductId, setAttributesSelectedProductId] = useState(null);
  const [attributesSearchAttempted, setAttributesSearchAttempted] = useState(false);
  const [categoryIdsToAssign, setCategoryIdsToAssign] = useState([]);
  const [categoryAction, setCategoryAction] = useState("add");
  const [categoryPickerModalOpen, setCategoryPickerModalOpen] = useState(false);
  const [clearGalleryImages, setClearGalleryImages] = useState(false);
  const [clearGalleryFiles, setClearGalleryFiles] = useState(false);
  const [deleteAllRelatedProducts, setDeleteAllRelatedProducts] = useState(false);
  const [relatedProductsToAdd, setRelatedProductsToAdd] = useState([]);
  const [replaceTextRules, setReplaceTextRules] = useState([]);
  const filterDropdownRef = useRef(null);
  const step2CardRef = useRef(null);
  const [uploadMenuProductId, setUploadMenuProductId] = useState(null);
  const uploadMenuContainerRef = useRef(null);
  const productImageInputRef = useRef(null);
  const galleryImageInputRef = useRef(null);
  const downloadableFileInputRef = useRef(null);
  const uploadTargetProductIdRef = useRef(null);
  const uploadTypeRef = useRef(null);
  const [uploadingProductId, setUploadingProductId] = useState(null);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [applySuccessMessage, setApplySuccessMessage] = useState(null);
  const [historyBatches, setHistoryBatches] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertingBatchId, setRevertingBatchId] = useState(null);
  const [applyNote, setApplyNote] = useState("");
  const [rollbackDateFrom, setRollbackDateFrom] = useState("");
  const [rollbackDateTo, setRollbackDateTo] = useState("");
  const [rollbackNoteSearch, setRollbackNoteSearch] = useState("");
  const [rollbackSectionFilter, setRollbackSectionFilter] = useState("__all__");
  const setPropertyChecked = (key, checked) => {
    setPropertySelected((prev) => ({ ...prev, [key]: checked }));
  };
  const setPropertyValue = (key, value) => {
    setPropertyValues((prev) => ({ ...prev, [key]: value }));
  };
  const clearPropertiesSection = () => {
    setPropertySelected((prev) => {
      const next = { ...prev };
      PROPERTIES_SECTION_KEYS.forEach((k) => {
        next[k] = false;
      });
      return next;
    });
    setPropertyValues((prev) => ({ ...prev, ...INITIAL_PROPERTY_VALUES_FOR_SECTION }));
  };
  const propertiesSelectedCount = Object.keys(propertySelected).filter(
    (k) => propertySelected[k] && k !== "seoTitle" && k !== "seoDescription"
  ).length;
  const seoSelectedCount = [propertySelected.seoTitle, propertySelected.seoDescription].filter(Boolean).length;
  const fetchProductsInFlightRef = useRef(false);
  const fetchCategoriesInFlightRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (uploadMenuProductId == null) return;
    const handleClickOutside = (e) => {
      if (uploadMenuContainerRef.current && !uploadMenuContainerRef.current.contains(e.target)) {
        setUploadMenuProductId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [uploadMenuProductId]);

  const authData = getAuthData();
  const storeId = getStoreId();
  const apiUrl = import.meta.env.VITE_BACKEND_URL;

  useEffect(() => {
    if (step !== 2 || !authData?.store_id || !authData?.access_token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiUrl}/api/shipping-options?storeId=${authData.store_id}&token=${authData.access_token}`
        );
        const data = await res.json();
        if (cancelled) return;
        setShippingOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setShippingOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [step, apiUrl, authData?.store_id, authData?.access_token]);

  useEffect(() => {
    if (sectionTab !== SECTION_TAB_ROLLBACK || !storeId) return;
    let cancelled = false;
    setHistoryLoading(true);
    fetch(`${apiUrl}/api/bulk-product-update/history?id=${encodeURIComponent(storeId)}&limit=50&offset=0`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.batches) setHistoryBatches(data.batches);
      })
      .catch(() => { if (!cancelled) setHistoryBatches([]); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [sectionTab, storeId, apiUrl]);

  const fetchCategories = async () => {
    if (!authData?.store_id || !authData?.access_token) return;
    setCategoriesLoading(true);
    try {
      const all = [];
      let offset = 0;
      const limit = 100;
      let total = null;
      let hasMore = true;
      while (hasMore) {
        const res = await fetch(
          `${apiUrl}/api/categories?storeId=${authData.store_id}&token=${authData.access_token}&limit=${limit}&offset=${offset}`
        );
        const data = await res.json();
        if (!res.ok) break;
        if (total === null) total = data.total || 0;
        if (data.items?.length) {
          all.push(...data.items);
          offset += limit;
          hasMore = data.items.length === limit && all.length < total;
        } else hasMore = false;
      }
      setCategories(all);
    } catch (e) {
      logger.error("BulkProductUpdate fetchCategories", e);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const buildProductsQuery = (baseUrl, opts = {}) => {
    const { limit = itemsPerPage, offset = (currentPage - 1) * itemsPerPage } = opts;
    let url = `${baseUrl}limit=${limit}&offset=${offset}`;
    if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
    if (keywordApplied) url += `&keyword=${encodeURIComponent(keywordApplied)}`;
    if (skuApplied) url += `&sku=${encodeURIComponent(skuApplied)}`;
    if (priceFromApplied !== "") url += `&priceFrom=${encodeURIComponent(priceFromApplied)}`;
    if (priceToApplied !== "") url += `&priceTo=${encodeURIComponent(priceToApplied)}`;
    if (enabledApplied === true) url += "&enabled=true";
    if (enabledApplied === false) url += "&enabled=false";
    if (inStockApplied === true) url += "&inStock=true";
    if (inStockApplied === false) url += "&inStock=false";
    if (createdFromApplied) url += `&createdFrom=${encodeURIComponent(createdFromApplied)}`;
    if (createdToApplied) url += `&createdTo=${encodeURIComponent(createdToApplied)}`;
    if (updatedFromApplied) url += `&updatedFrom=${encodeURIComponent(updatedFromApplied)}`;
    if (updatedToApplied) url += `&updatedTo=${encodeURIComponent(updatedToApplied)}`;
    if (attributeNameApplied && attributeValueApplied) {
      url += `&attributeName=${encodeURIComponent(attributeNameApplied)}&attributeValue=${encodeURIComponent(attributeValueApplied)}`;
    }
    if (sortField) url += `&sort=${sortField}&direction=${sortDirection}`;
    return url;
  };

  const fetchProducts = async () => {
    if (!authData?.store_id || !authData?.access_token) return;
    if (fetchProductsInFlightRef.current) return;
    fetchProductsInFlightRef.current = true;
    setLoading(true);
    try {
      const base = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&`;
      const url = buildProductsQuery(base, { limit: itemsPerPage, offset: (currentPage - 1) * itemsPerPage });
      const res = await fetch(url);
      const data = await res.json();
      if (res.ok) {
        const items = data.items || [];
        setProducts(Array.isArray(items) ? items : []);
        setTotalProducts(data.total ?? items.length);
      }
    } catch (e) {
      logger.error("BulkProductUpdate fetchProducts", e);
    } finally {
      setLoading(false);
      fetchProductsInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!authData?.store_id || !authData?.access_token) return;
    if (fetchCategoriesInFlightRef.current) return;
    fetchCategoriesInFlightRef.current = true;
    fetchCategories().finally(() => { fetchCategoriesInFlightRef.current = false; });
  }, [authData?.store_id, authData?.access_token, apiUrl]);

  useEffect(() => {
    fetchProducts();
  }, [
    currentPage,
    itemsPerPage,
    selectedCategory,
    keywordApplied,
    skuApplied,
    priceFromApplied,
    priceToApplied,
    enabledApplied,
    inStockApplied,
    createdFromApplied,
    createdToApplied,
    updatedFromApplied,
    updatedToApplied,
    attributeNameApplied,
    attributeValueApplied,
    sortField,
    sortDirection
  ]);

  const applyFilters = () => {
    setKeywordApplied(keyword.trim());
    setCurrentPage(1);
  };

  const handleAddFilter = () => {
    const v = filterInputValue.trim();
    const vPriceTo = filterPriceToValue.trim();
    if (selectedFilterType === "keyword") {
      setKeywordApplied(v);
      setFilterInputValue("");
    } else if (selectedFilterType === "category") {
      // selectedCategory is bound to the select; already applied
    } else if (selectedFilterType === "sku") {
      setSkuApplied(v);
      setFilterInputValue("");
    } else if (selectedFilterType === "price") {
      if (v !== "") setPriceFromApplied(v);
      if (vPriceTo !== "") setPriceToApplied(vPriceTo);
      setFilterInputValue("");
      setFilterPriceToValue("");
    } else if (selectedFilterType === "availability") {
      // value in input: "in stock" / "out of stock" or we use a separate select; for now accept "in", "out", "true", "false"
      const lower = v.toLowerCase();
      if (lower === "in stock" || lower === "in" || lower === "true" || lower === "1") setInStockApplied(true);
      else if (lower === "out of stock" || lower === "out" || lower === "false" || lower === "0") setInStockApplied(false);
      setFilterInputValue("");
    } else if (selectedFilterType === "enableDisable") {
      const lower = v.toLowerCase();
      if (lower === "enabled" || lower === "enable" || lower === "true" || lower === "1") setEnabledApplied(true);
      else if (lower === "disabled" || lower === "disable" || lower === "false" || lower === "0") setEnabledApplied(false);
      setFilterInputValue("");
    } else if (selectedFilterType === "creationDate" || selectedFilterType === "modificationDate") {
      // Date range is applied directly via the date inputs; Add is a no-op
    } else if (selectedFilterType === "attribute") {
      setAttributeNameApplied(v);
      setAttributeValueApplied(filterAttributeValue.trim());
      setFilterInputValue("");
      setFilterAttributeValue("");
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setKeyword("");
    setKeywordApplied("");
    setFilterInputValue("");
    setFilterPriceToValue("");
    setSelectedCategory("");
    setSkuApplied("");
    setPriceFromApplied("");
    setPriceToApplied("");
    setEnabledApplied(null);
    setInStockApplied(null);
    setCreatedFromApplied("");
    setCreatedToApplied("");
    setUpdatedFromApplied("");
    setUpdatedToApplied("");
    setAttributeNameApplied("");
    setAttributeValueApplied("");
    setFilterAttributeValue("");
    setSortField("");
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    !!keywordApplied ||
    !!selectedCategory ||
    !!skuApplied ||
    priceFromApplied !== "" ||
    priceToApplied !== "" ||
    enabledApplied !== null ||
    inStockApplied !== null ||
    !!createdFromApplied ||
    !!createdToApplied ||
    !!updatedFromApplied ||
    !!updatedToApplied ||
    (!!attributeNameApplied && !!attributeValueApplied) ||
    !!sortField;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectPage = () => {
    const pageIds = new Set(products.map((p) => p.id));
    const allPageSelected = products.length > 0 && products.every((p) => selectedIds.has(p.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectAllMatchedFull = async () => {
    if (selectedIds.size === totalProducts) {
      setSelectedIds(new Set());
      return;
    }
    const allIds = new Set();
    let offset = 0;
    const limit = 100;
    const base = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&`;
    while (offset < totalProducts) {
      const url = buildProductsQuery(base, { limit, offset });
      const res = await fetch(url);
      const data = await res.json();
      const items = data.items || [];
      items.forEach((p) => allIds.add(p.id));
      offset += items.length;
      if (items.length < limit) break;
    }
    setSelectedIds(allIds);
  };

  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const productImageUrl = (p) =>
    p?.defaultImage?.url || p?.media?.images?.[0]?.url || p?.imageUrl || "";

  const goToStep2 = () => {
    if (selectedIds.size === 0) return;
    setStep(2);
  };

  const triggerUpload = (productId, type) => {
    uploadTargetProductIdRef.current = productId;
    uploadTypeRef.current = type;
    setUploadMenuProductId(null);
    if (type === "productImage") productImageInputRef.current?.click();
    else if (type === "galleryImage") galleryImageInputRef.current?.click();
    else if (type === "downloadable") downloadableFileInputRef.current?.click();
  };

  const handleUploadFile = async (e, type) => {
    const file = e.target.files?.[0];
    const productId = uploadTargetProductIdRef.current;
    e.target.value = "";
    uploadTargetProductIdRef.current = null;
    uploadTypeRef.current = null;

    if (!file || productId == null) return;
    if (!authData?.store_id || !authData?.access_token) {
      addToast(t("bulkProductUpdate.authRequired"), "error");
      return;
    }

    const apiUrlBase = import.meta.env.VITE_BACKEND_URL;
    const params = new URLSearchParams({ storeId: authData.store_id, token: authData.access_token });
    let endpoint;
    if (type === "productImage") endpoint = `${apiUrlBase}/api/products/${productId}/upload-image?${params}`;
    else if (type === "galleryImage") endpoint = `${apiUrlBase}/api/products/${productId}/upload-gallery?${params}`;
    else if (type === "downloadable") {
      params.set("fileName", file.name);
      endpoint = `${apiUrlBase}/api/products/${productId}/upload-file?${params}`;
    } else return;

    setUploadingProductId(productId);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(endpoint, { method: "POST", body: formData });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const key =
          type === "productImage"
            ? "bulkProductUpdate.uploadProductImageSuccess"
            : type === "galleryImage"
              ? "bulkProductUpdate.uploadGalleryImageSuccess"
              : "bulkProductUpdate.uploadDownloadableFileSuccess";
        addToast(t(key));
        fetchProducts();
      } else {
        addToast(data.error || t("bulkProductUpdate.uploadError"), "error");
      }
    } catch (err) {
      logger.error("BulkProductUpdate handleUploadFile", err);
      addToast(t("bulkProductUpdate.uploadError"), "error");
    } finally {
      setUploadingProductId(null);
    }
  };

  const handleApplyChanges = async () => {
    if (!authData?.store_id || !authData?.access_token) {
      addToast(t("bulkProductUpdate.authRequired"), "error");
      return;
    }
    const hasCategoryRules = categoryIdsToAssign.length > 0;
    const hasRelatedRules = deleteAllRelatedProducts || relatedProductsToAdd.length > 0;
    const hasShippingRules = !!shippingConfig;
    const hasPricingRules =
      pricingRevertSalesPrice || (pricingRule && parseFloat(pricingRule.value) !== 0);
    const hasWholesaleRules =
      pricingClearWholesale ||
      pricingWholesaleRules.some((rule) => (parseInt(rule.quantity, 10) || 0) > 0);
    const hasPropertyRules = propertiesSelectedCount > 0 || seoSelectedCount > 0;
    const hasReplaceTextRules = replaceTextRules.length > 0;
    const hasAttributeRules = attributeRules.length > 0;
    if (!clearGalleryImages && !clearGalleryFiles && !hasCategoryRules && !hasRelatedRules && !hasShippingRules && !hasPricingRules && !hasWholesaleRules && !hasPropertyRules && !hasReplaceTextRules && !hasAttributeRules) return;
    const productIds = Array.from(selectedIds);
    if (productIds.length === 0) return;

    if (storeId) {
      try {
        const quotaRes = await fetch(
          `${apiUrl}/api/bulk-product-update/quota?id=${encodeURIComponent(storeId)}&count=${productIds.length}`
        );
        const quotaData = await quotaRes.json().catch(() => ({}));
        if (quotaRes.ok && quotaData.allowed === false) {
          await showQuotaExceededAlert(
            t("common.quotaExceededTitle"),
            t("bulkProductUpdate.quotaExceeded"),
            t("common.ok")
          );
          return;
        }
      } catch (_) {
        // Proceed if quota check fails (e.g. network)
      }
    }

    setApplyingChanges(true);
    const apiUrlBase = import.meta.env.VITE_BACKEND_URL;
    const params = new URLSearchParams({ storeId: authData.store_id, token: authData.access_token });

    // Create batch + snapshots for history/revert (before any PUTs)
    const sectionsApplied = [];
    if (propertiesSelectedCount > 0) sectionsApplied.push("properties");
    if (seoSelectedCount > 0) sectionsApplied.push("seo");
    if (hasReplaceTextRules) sectionsApplied.push("replaceText");
    if (hasRelatedRules) sectionsApplied.push("related");
    if (hasShippingRules) sectionsApplied.push("shipping");
    if (hasPricingRules || hasWholesaleRules) sectionsApplied.push("pricing");
    if (hasCategoryRules) sectionsApplied.push("categories");
    if (hasAttributeRules) sectionsApplied.push("attributes");
    if (clearGalleryImages || clearGalleryFiles) sectionsApplied.push("images");
    if (storeId) {
      try {
        await fetch(`${apiUrlBase}/api/bulk-product-update/begin-apply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: storeId,
            product_ids: productIds.map((id) => Number(id)),
            sections_applied: [...new Set(sectionsApplied)],
            note: (applyNote || "").trim() || applyNoteSummary || undefined,
          }),
        });
      } catch (_) {
        // Proceed with apply even if snapshot fails (history/revert may be incomplete)
      }
    }
    let galleryOk = 0;
    let galleryErr = 0;
    let filesOk = 0;
    let filesErr = 0;
    let categoriesOk = 0;
    let categoriesErr = 0;
    let relatedOk = 0;
    let relatedErr = 0;
    let shippingOk = 0;
    let shippingErr = 0;
    let pricingOk = 0;
    let pricingErr = 0;
    let propertiesOk = 0;
    let propertiesErr = 0;
    let replaceTextOk = 0;
    let replaceTextErr = 0;
    let attributesOk = 0;
    let attributesErr = 0;

    const relatedIdsToAdd = relatedProductsToAdd.map((p) => Number(p.id));

    // Resolved product class ID for Properties apply: only set when the store has that class (avoids Ecwid 404 "ProductClass not found").
    let resolvedProductClassId = null;

    // Build PUT body for Ecwid Update product API: https://docs.ecwid.com/api-reference/rest-api/products/update-product
    const buildPropertiesBody = () => {
      const body = {};
      if (propertySelected.price) body.price = parseFloat(propertyValues.price) || 0;
      if (propertySelected.compareToPrice) body.compareToPrice = parseFloat(propertyValues.compareToPrice) || 0;
      if (propertySelected.weight) body.weight = parseFloat(propertyValues.weight) || 0;
      if (propertySelected.dimensions) {
        body.dimensions = {
          length: parseFloat(propertyValues.length) || 0,
          width: parseFloat(propertyValues.width) || 0,
          height: parseFloat(propertyValues.height) || 0,
        };
      }
      if (propertySelected.quantity) {
        body.unlimited = !!propertyValues.unlimited;
        if (!propertyValues.unlimited) body.quantity = parseInt(propertyValues.quantity, 10) || 0;
        // When unlimited is true, quantity is omitted per API
      }
      if (propertySelected.stockWarning) body.warningLimit = parseInt(propertyValues.stockWarning, 10) || 0;
      if (propertySelected.enabled) body.enabled = !!propertyValues.enabled;
      if (propertySelected.shippingRequired) body.isShippingRequired = !!propertyValues.shippingRequired;
      if (propertySelected.seoTitle) body.seoTitle = String(propertyValues.seoTitle ?? "").trim() || "";
      if (propertySelected.seoDescription) body.seoDescription = String(propertyValues.seoDescription ?? "").trim() || "";
      if (propertySelected.productType && resolvedProductClassId != null) body.productClassId = resolvedProductClassId;
      return body;
    };

    const roundPrice = (value, resolution, mode) => {
      const res = parseFloat(resolution) || 0.01;
      if (res <= 0) return value;
      let r;
      if (mode === "up") r = Math.ceil(value / res) * res;
      else if (mode === "down") r = Math.floor(value / res) * res;
      else r = Math.round(value / res) * res;
      return Math.round(r * 100) / 100;
    };

    let conflict409Message = null;
    const setConflictIf409 = async (res) => {
      if (res.status === 409 && !conflict409Message) {
        const data = await res.json().catch(() => ({}));
        conflict409Message = data.error || data.errorMessage || t("bulkProductUpdate.conflict409");
      }
    };

    try {
      if (hasPropertyRules) {
        // Resolve product type to a class ID that exists in this store (Ecwid returns 404 if class doesn't exist).
        if (propertySelected.productType) {
          try {
            const classesRes = await fetch(`${apiUrlBase}/api/classes?${params}`);
            const classesList = classesRes.ok ? await classesRes.json() : [];
            const classIds = new Set(
              (Array.isArray(classesList) ? classesList : []).map((c) => Number(c.id)).filter((n) => !Number.isNaN(n))
            );
            const wantDigital = propertyValues.productType === "digital";
            if (wantDigital && classIds.has(1)) resolvedProductClassId = 1;
            else if (!wantDigital && classIds.has(0)) resolvedProductClassId = 0;
            if (resolvedProductClassId == null) {
              addToast(t("bulkProductUpdate.productTypeNotAvailable"), "warning");
            }
          } catch (_) {
            // If we can't load classes, don't set productClassId to avoid 404s
          }
        }
        const seoTemplatesUsed =
          (propertySelected.seoTitle && /\{[^}]+\}/.test(String(propertyValues.seoTitle ?? ""))) ||
          (propertySelected.seoDescription && /\{[^}]+\}/.test(String(propertyValues.seoDescription ?? "")));
        for (const productId of productIds) {
          try {
            let body = buildPropertiesBody();
            if (seoTemplatesUsed) {
              const getRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`);
              if (!getRes.ok) {
                propertiesErr += 1;
                await setConflictIf409(getRes);
                continue;
              }
              const product = await getRes.json();
              if (propertySelected.seoTitle) body.seoTitle = resolveSeoTemplate(String(propertyValues.seoTitle ?? "").trim(), product, categories);
              if (propertySelected.seoDescription) body.seoDescription = resolveSeoTemplate(String(propertyValues.seoDescription ?? "").trim(), product, categories);
            }
            const updateRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              }
            );
            if (updateRes.ok) propertiesOk += 1;
            else {
              propertiesErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            propertiesErr += 1;
          }
        }
      }

      if (hasReplaceTextRules) {
        const textFields = ["name", "description", "shortDescription", "seoTitle", "seoDescription"];
        for (const productId of productIds) {
          try {
            const getRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`);
            if (!getRes.ok) {
              replaceTextErr += 1;
              await setConflictIf409(getRes);
              continue;
            }
            const product = await getRes.json();
            const current = {};
            textFields.forEach((f) => {
              current[f] = product[f] ?? "";
            });
            replaceTextRules.forEach((rule) => {
              const val = current[rule.field];
              current[rule.field] = applyReplaceRule(val, rule);
            });
            const body = {};
            textFields.forEach((f) => {
              if (current[f] !== (product[f] ?? "")) body[f] = current[f];
            });
            if (Object.keys(body).length === 0) {
              replaceTextOk += 1;
              continue;
            }
            const updateRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            });
            if (updateRes.ok) replaceTextOk += 1;
            else {
              replaceTextErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            replaceTextErr += 1;
          }
        }
      }

      if (hasAttributeRules) {
        for (const productId of productIds) {
          try {
            const getRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`);
            if (!getRes.ok) {
              attributesErr += 1;
              await setConflictIf409(getRes);
              continue;
            }
            const product = await getRes.json();
            const currentAttrs = Array.isArray(product.attributes) ? product.attributes : [];
            const newAttrs = currentAttrs.map((a) => {
              const rule = attributeRules.find(
                (r) => (String(a.name || "").toLowerCase()) === (String(r.name || "").toLowerCase())
              );
              if (rule) return { ...a, value: rule.value };
              return a;
            });
            for (const rule of attributeRules) {
              const matched = newAttrs.some(
                (a) => (String(a.name || "").toLowerCase()) === (String(rule.name || "").toLowerCase())
              );
              if (!matched) newAttrs.push({ name: rule.name, value: rule.value });
            }
            const updateRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ attributes: newAttrs }),
            });
            if (updateRes.ok) attributesOk += 1;
            else {
              attributesErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            attributesErr += 1;
          }
        }
        setAttributeRules([]);
      }

      if (hasRelatedRules) {
        for (const productId of productIds) {
          try {
            const getRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`
            );
            if (!getRes.ok) {
              relatedErr += 1;
              continue;
            }
            const product = await getRes.json();
            const currentIds = (Array.isArray(product.relatedProducts?.productIds) ? product.relatedProducts.productIds : []).map((id) => Number(id));
            let newProductIds = deleteAllRelatedProducts ? [] : [...currentIds];
            for (const id of relatedIdsToAdd) {
              if (id !== Number(productId) && !newProductIds.includes(id)) newProductIds.push(id);
            }
            const updateRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  relatedProducts: { productIds: newProductIds },
                }),
              }
            );
            if (updateRes.ok) relatedOk += 1;
            else {
              relatedErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            relatedErr += 1;
          }
        }
        setDeleteAllRelatedProducts(false);
        setRelatedProductsToAdd([]);
      }

      if (hasCategoryRules) {
        const toAddOrRemove = categoryIdsToAssign.map((id) => Number(id));
        const isAdd = categoryAction === "add";
        for (const productId of productIds) {
          try {
            const getRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`
            );
            if (!getRes.ok) {
              categoriesErr += 1;
              continue;
            }
            const product = await getRes.json();
            const currentIds = (Array.isArray(product.categoryIds) ? product.categoryIds : []).map((id) => Number(id));
            let newCategoryIds;
            if (isAdd) {
              newCategoryIds = [...new Set([...currentIds, ...toAddOrRemove])];
            } else {
              const toRemoveSet = new Set(toAddOrRemove);
              newCategoryIds = currentIds.filter((id) => !toRemoveSet.has(id));
            }
            if (newCategoryIds.length === 0) {
              categoriesErr += 1;
              continue;
            }
            const updateRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  categoryIds: newCategoryIds,
                  defaultCategoryId: newCategoryIds[0] || 0,
                }),
              }
            );
            if (updateRes.ok) categoriesOk += 1;
            else {
              categoriesErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            categoriesErr += 1;
          }
        }
        setCategoryIdsToAssign([]);
      }

      if (hasShippingRules) {
        let shippingPayload;
        if (shippingConfig === "store") {
          shippingPayload = {
            type: "GLOBAL_METHODS",
            methodMarkup: shippingStoreFixedFee ? parseFloat(shippingStoreFixedFeeAmount) || 0 : 0,
          };
        } else if (shippingConfig === "specify") {
          shippingPayload = {
            type: "SELECTED_METHODS",
            enabledMethods: shippingSpecifyMode === "allow" ? [...shippingSelectedMethodIds] : [],
            disabledMethods: shippingSpecifyMode === "disallow" ? [...shippingSelectedMethodIds] : [],
          };
        } else if (shippingConfig === "fixedRate") {
          shippingPayload = {
            type: "FLAT_RATE",
            flatRate: parseFloat(shippingFixedRateAmount) || 0,
          };
        } else if (shippingConfig === "free") {
          shippingPayload = { type: "FREE_SHIPPING" };
        } else {
          shippingPayload = null;
        }
        if (shippingPayload) {
          for (const productId of productIds) {
            try {
              const updateRes = await fetch(
                `${apiUrlBase}/api/products/${productId}?${params}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ shipping: shippingPayload }),
                }
              );
              if (updateRes.ok) shippingOk += 1;
              else {
                shippingErr += 1;
                await setConflictIf409(updateRes);
              }
            } catch (_) {
              shippingErr += 1;
            }
          }
          setShippingConfig("");
          setShippingStoreFixedFee(false);
          setShippingStoreFixedFeeAmount("");
          setShippingSelectedMethodIds([]);
          setShippingFixedRateAmount("");
        }
      }

      if (hasPricingRules || hasWholesaleRules) {
        const resolution = parseFloat(pricingRoundingResolution) || 0.01;
        const doRound = (v) =>
          pricingModificationDetailsOn ? roundPrice(v, resolution, pricingRoundingMode) : Math.round(v * 100) / 100;

        const buildWholesalePayload = (tiers, basePrice) => {
          return tiers
            .map((t) => {
              const qty = parseInt(t.quantity, 10) || 0;
              const val = parseFloat(t.price) || 0;
              // "%" = reduced BY X% → tier price = basePrice * (100 - X) / 100 (e.g. 20% off = pay 80% of base)
              const price =
                t.type === "%" && basePrice != null
                  ? doRound((basePrice * Math.max(0, 100 - val)) / 100)
                  : val;
              return { quantity: qty, price };
            })
            .filter((t) => t.quantity > 0)
            .sort((a, b) => a.quantity - b.quantity);
        };

        const wholesaleRulesToApply = pricingWholesaleRules.filter(
          (rule) => (parseInt(rule.quantity, 10) || 0) > 0
        );

        for (const productId of productIds) {
          try {
            const getRes = await fetch(`${apiUrlBase}/api/products/${productId}?${params}`);
            if (!getRes.ok) {
              pricingErr += 1;
              continue;
            }
            const product = await getRes.json();
            const body = {};
            const productPrice = parseFloat(product.price) || 0;

            if (hasPricingRules) {
              const currentPrice = productPrice;
              const currentCompareTo = product.compareToPrice != null ? parseFloat(product.compareToPrice) : null;
              let newPrice;
              let newCompareTo;
              if (pricingRevertSalesPrice) {
                newPrice = currentCompareTo != null ? currentCompareTo : currentPrice;
                newCompareTo = 0;
              } else {
                const oldPrice = currentPrice;
                const rule = pricingRule || { type: "%", sign: "+", value: "0" };
                const val = parseFloat(rule.value) || 0;
                const delta = rule.type === "%" ? (currentPrice * val) / 100 : val;
                let p = rule.sign === "-" ? currentPrice - delta : currentPrice + delta;
                p = p < 0 ? 0 : p;
                newPrice = doRound(p);
                newCompareTo = pricingSaveOldPriceToCompareAt ? doRound(oldPrice) : (currentCompareTo != null ? currentCompareTo : 0);
              }
              body.price = newPrice;
              body.compareToPrice = newCompareTo;
            }

            if (hasWholesaleRules) {
              const effectiveProductPrice = body.price != null ? body.price : productPrice;
              const hasPercentTiers = wholesaleRulesToApply.some((rule) => rule.type === "%");
              let wholesaleList = [];
              if (wholesaleRulesToApply.length > 0) {
                wholesaleList = buildWholesalePayload(
                  wholesaleRulesToApply,
                  hasPercentTiers ? productPrice : null
                );
                if (wholesaleList.length > 0 && effectiveProductPrice != null) {
                  wholesaleList = wholesaleList.map((t) => ({
                    ...t,
                    price: Math.min(t.price, effectiveProductPrice),
                  }));
                }
              }
              // Ecwid PUT replaces the full wholesalePrices array — new rules replace existing tiers.
              // Clear-only (no new rules) sends [] to remove all wholesale prices.
              body.wholesalePrices = wholesaleList;
            }

            const updateRes = await fetch(
              `${apiUrlBase}/api/products/${productId}?${params}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              }
            );
            if (updateRes.ok) pricingOk += 1;
            else {
              pricingErr += 1;
              await setConflictIf409(updateRes);
            }
          } catch (_) {
            pricingErr += 1;
          }
        }
        setPricingRule({ type: "%", sign: "+", value: "0" });
        setPricingSaveOldPriceToCompareAt(false);
        setPricingRevertSalesPrice(false);
        setPricingWholesaleRules([]);
        setPricingClearWholesale(false);
      }

      if (clearGalleryImages) {
        for (const productId of productIds) {
          try {
            const res = await fetch(
              `${apiUrlBase}/api/products/${productId}/gallery?${params}`,
              { method: "DELETE" }
            );
            if (res.ok) galleryOk += 1;
            else galleryErr += 1;
          } catch (_) {
            galleryErr += 1;
          }
        }
      }
      if (clearGalleryFiles) {
        for (const productId of productIds) {
          try {
            const res = await fetch(
              `${apiUrlBase}/api/products/${productId}/files?${params}`,
              { method: "DELETE" }
            );
            if (res.ok) filesOk += 1;
            else filesErr += 1;
          } catch (_) {
            filesErr += 1;
          }
        }
      }

      setClearGalleryImages(false);
      setClearGalleryFiles(false);

      if (storeId && productIds.length > 0) {
        try {
          await fetch(`${apiUrl}/api/bulk-product-update/consume-quota`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: storeId, amount: productIds.length }),
          });
        } catch (_) {
          // Log only; apply already succeeded
        }
      }

      // Reset all step-2 form data after successful apply
      clearPropertiesSection();
      setPropertySelected((prev) => ({ ...prev, seoTitle: false, seoDescription: false }));
      setPropertyValues((prev) => ({ ...prev, seoTitle: "{product_name}", seoDescription: "{description}" }));
      setReplaceTextRules([]);
      setCategoryIdsToAssign([]);
      setCategoryAction("add");
      setDeleteAllRelatedProducts(false);
      setRelatedProductsToAdd([]);
      setShippingConfig("");
      setShippingStoreFixedFee(false);
      setShippingStoreFixedFeeAmount("");
      setShippingSpecifyMode("allow");
      setShippingSelectedMethodIds([]);
      setShippingFixedRateAmount("");
      setPricingRule({ type: "%", sign: "+", value: "0" });
      setPricingSaveOldPriceToCompareAt(false);
      setPricingRevertSalesPrice(false);
      setPricingWholesaleRules([]);
      setPricingClearWholesale(false);
      setClearGalleryImages(false);
      setClearGalleryFiles(false);
      setApplyNote("");

      // Show success: banner (guaranteed) + toast after a short delay
      const successMsg = conflict409Message || t("bulkProductUpdate.applySuccess");
      setApplySuccessMessage(successMsg);
      setTimeout(() => setApplySuccessMessage(null), 6000);
      setTimeout(() => addToast(successMsg, "success", 6000), 150);
    } catch (e) {
      logger.error("BulkProductUpdate handleApplyChanges", e);
      addToast(t("bulkProductUpdate.applyError"), "error", 3000, { anchor: applyButtonRef, anchorPosition: "above" });
    } finally {
      setApplyingChanges(false);
    }
  };

  const propertiesValidationError =
    propertiesSelectedCount > 0 || seoSelectedCount > 0
      ? getPropertiesValidationError(propertySelected, propertyValues, t)
      : null;
  const canApply =
    (clearGalleryImages ||
      clearGalleryFiles ||
      categoryIdsToAssign.length > 0 ||
      deleteAllRelatedProducts ||
      relatedProductsToAdd.length > 0 ||
      !!shippingConfig ||
      pricingRevertSalesPrice ||
      (pricingRule && parseFloat(pricingRule.value) !== 0) ||
      pricingClearWholesale ||
      pricingWholesaleRules.some((rule) => (parseInt(rule.quantity, 10) || 0) > 0) ||
      propertiesSelectedCount > 0 ||
      seoSelectedCount > 0 ||
      replaceTextRules.length > 0 ||
      attributeRules.length > 0) &&
    selectedIds.size > 0 &&
    !applyingChanges &&
    !propertiesValidationError;

  /** Section-wise change summary for the Apply confirmation modal. */
  const applyChangeSummary = useMemo(() => {
    const sections = [];
    const add = (sectionId, labelKey, items) => {
      if (items.length > 0) sections.push({ sectionId, labelKey, items });
    };

    const propKeys = PROPERTIES_SECTION_KEYS.filter((k) => propertySelected[k]);
    if (propKeys.length > 0) {
      add(
        "properties",
        "bulkProductUpdate.sectionProperties",
        propKeys.map((key) => {
          const label = t(`bulkProductUpdate.prop${key.charAt(0).toUpperCase() + key.slice(1)}`);
          const value = getPropertyChangeSummary(key, propertyValues, t);
          return `${label}: ${value}`;
        })
      );
    }
    if (propertySelected.seoTitle || propertySelected.seoDescription) {
      const items = [];
      if (propertySelected.seoTitle) {
        const v = String(propertyValues.seoTitle ?? "").trim();
        items.push(t("bulkProductUpdate.propSeoTitle") + ": " + (v ? (v.length > 50 ? v.slice(0, 50) + "…" : v) : "—"));
      }
      if (propertySelected.seoDescription) {
        const v = String(propertyValues.seoDescription ?? "").trim();
        items.push(t("bulkProductUpdate.propSeoDescription") + ": " + (v ? (v.length > 60 ? v.slice(0, 60) + "…" : v) : "—"));
      }
      add("seo", "bulkProductUpdate.sectionSeo", items);
    }
    if (replaceTextRules.length > 0) {
      const items = replaceTextRules.map((rule) => {
        const fieldOpt = REPLACE_TEXT_FIELD_OPTIONS.find((o) => o.value === rule.field);
        const opOpt = REPLACE_TEXT_OPERATION_OPTIONS.find((o) => o.value === rule.operation);
        const fieldName = fieldOpt ? t("bulkProductUpdate." + fieldOpt.labelKey) : rule.field;
        const opName = opOpt ? t("bulkProductUpdate." + opOpt.labelKey) : rule.operation;
        if (rule.operation === "erase") return `${fieldName}: ${opName}`;
        if (rule.operation === "find_replace")
          return `${fieldName} — ${opName}: "${String(rule.findText || "").slice(0, 30)}${(rule.findText || "").length > 30 ? "…" : ""}" → "${String(rule.replaceText || "").slice(0, 30)}${(rule.replaceText || "").length > 30 ? "…" : ""}"`;
        if (rule.operation === "replace_entire") return `${fieldName}: ${opName} → "${String(rule.replaceText || "").slice(0, 40)}…"`;
        return `${fieldName}: ${opName}`;
      });
      add("replaceText", "bulkProductUpdate.sectionReplaceText", items);
    }
    if (attributeRules.length > 0) {
      const items = attributeRules.map((r) => `${r.name || "—"}: ${r.value ?? ""}`);
      add("attributes", "bulkProductUpdate.sectionAttributes", items);
    }
    if (categoryIdsToAssign.length > 0) {
      const actionLabel =
        categoryAction === "add"
          ? t("bulkProductUpdate.categoriesActionAdd")
          : t("bulkProductUpdate.categoriesActionRemove");
      const names = categoryIdsToAssign
        .map((id) => categories.find((c) => Number(c.id) === Number(id))?.name)
        .filter(Boolean);
      const detail = names.length > 0 ? names.slice(0, 5).join(", ") + (names.length > 5 ? ` (+${names.length - 5})` : "") : `${categoryIdsToAssign.length} ${t("bulkProductUpdate.rollbackProducts")}`;
      add("categories", "bulkProductUpdate.sectionCategories", [actionLabel + " — " + detail]);
    }
    if (deleteAllRelatedProducts || relatedProductsToAdd.length > 0) {
      const items = [];
      if (deleteAllRelatedProducts) items.push(t("bulkProductUpdate.relatedDeleteAll"));
      if (relatedProductsToAdd.length > 0)
        items.push(t("bulkProductUpdate.relatedProductsToAdd") + ": " + relatedProductsToAdd.length + " " + t("bulkProductUpdate.rollbackProducts"));
      add("related", "bulkProductUpdate.sectionRelated", items);
    }
    if (shippingConfig) {
      const items = [];
      if (shippingConfig === "store") {
        items.push(t("bulkProductUpdate.shippingUseStore"));
        if (shippingStoreFixedFee && shippingStoreFixedFeeAmount)
          items.push(t("bulkProductUpdate.shippingAddFixedFee") + ": " + shippingStoreFixedFeeAmount);
      } else if (shippingConfig === "specify") {
        items.push(
          t("bulkProductUpdate.shippingSpecifyMethods") + " — " + (shippingSpecifyMode === "allow" ? t("bulkProductUpdate.shippingAllow") : t("bulkProductUpdate.shippingDisallow")) + " " + (shippingSelectedMethodIds?.length || 0) + " " + t("bulkProductUpdate.rollbackProducts")
        );
      } else if (shippingConfig === "fixedRate") {
        items.push(t("bulkProductUpdate.shippingFixedRatePerItem") + ": " + (shippingFixedRateAmount ?? "—"));
      } else if (shippingConfig === "free") {
        items.push(t("bulkProductUpdate.shippingFreeShipping"));
      }
      if (items.length > 0) add("shipping", "bulkProductUpdate.sectionShipping", items);
    }
    const hasPricing =
      pricingRevertSalesPrice ||
      (pricingRule && parseFloat(pricingRule.value) !== 0) ||
      pricingClearWholesale ||
      pricingWholesaleRules.some((rule) => (parseInt(rule.quantity, 10) || 0) > 0);
    if (hasPricing) {
      const items = [];
      if (pricingRevertSalesPrice) items.push(t("bulkProductUpdate.pricingRevertSalesPrice"));
      if (pricingRule && parseFloat(pricingRule.value) !== 0) {
        const dir = pricingRule.sign === "+" ? t("bulkProductUpdate.pricingDirectionIncrease") : t("bulkProductUpdate.pricingDirectionDecrease");
        if (pricingRule.type === "%")
          items.push(t("bulkProductUpdate.pricingChangeSummaryPercent", { direction: dir, value: pricingRule.value || "0" }));
        else items.push(t("bulkProductUpdate.pricingChangeSummaryAbsolute", { direction: dir, value: pricingRule.value || "0" }));
      }
      if (pricingClearWholesale) items.push(t("bulkProductUpdate.pricingWholesaleClearAll"));
      pricingWholesaleRules
        .filter((rule) => (parseInt(rule.quantity, 10) || 0) > 0)
        .forEach((rule) => {
          items.push(
            t("bulkProductUpdate.pricingWholesaleSummaryText", {
              quantity: rule.quantity || "0",
              amount: rule.type === "%" ? `${rule.price ?? "—"} %` : `${rule.price ?? "—"} $`,
            })
          );
        });
      add("pricing", "bulkProductUpdate.sectionPricing", items);
    }
    if (clearGalleryImages || clearGalleryFiles) {
      const items = [];
      if (clearGalleryImages) items.push(t("bulkProductUpdate.clearGalleryDeleteImages"));
      if (clearGalleryFiles) items.push(t("bulkProductUpdate.clearGalleryDeleteFiles"));
      add("images", "bulkProductUpdate.sectionClearGallery", items);
    }
    return sections;
  }, [
    propertySelected,
    propertyValues,
    replaceTextRules,
    attributeRules,
    categoryIdsToAssign,
    categoryAction,
    categories,
    deleteAllRelatedProducts,
    relatedProductsToAdd,
    shippingConfig,
    shippingStoreFixedFee,
    shippingStoreFixedFeeAmount,
    shippingSpecifyMode,
    shippingSelectedMethodIds,
    shippingFixedRateAmount,
    pricingRule,
    pricingRevertSalesPrice,
    pricingClearWholesale,
    pricingWholesaleRules,
    clearGalleryImages,
    clearGalleryFiles,
    t,
  ]);

  /** Summary string for rollback note when user leaves note empty (same data as apply confirmation modal). */
  const applyNoteSummary = useMemo(() => {
    if (!applyChangeSummary || applyChangeSummary.length === 0) return "";
    return applyChangeSummary
      .map(({ labelKey, items }) => t(labelKey) + ": " + (items || []).join("; "))
      .join(". ");
  }, [applyChangeSummary, t]);

  const showApplyConfirmSwal = () => {
    const validationError =
      propertiesSelectedCount > 0 || seoSelectedCount > 0
        ? getPropertiesValidationError(propertySelected, propertyValues, t)
        : null;
    if (validationError) {
      addToast(
        t("bulkProductUpdate.propertyValueRequired", { field: t(`bulkProductUpdate.${validationError.fieldLabelKey}`) }),
        "error"
      );
      return;
    }
    const count = selectedIds.size;
    const intro = escapeHtml(t("bulkProductUpdate.applyConfirmProductCount", { count }));
    const summaryTitle = escapeHtml(t("bulkProductUpdate.summaryOfChanges"));
    const sectionsHtml = applyChangeSummary
      .map(
        ({ labelKey, items }) =>
          `<div class="border rounded p-3 bg-light mb-2"><div class="fw-semibold small text-primary mb-2">${escapeHtml(t(labelKey))}</div><ul style="list-style-type: disc; list-style-position: inside; padding-left: 0.5rem; margin: 0; font-size: 0.875rem;">${items.map((line) => `<li style="margin-bottom: 0.25rem;">${escapeHtml(line)}</li>`).join("")}</ul></div>`
      )
      .join("");
    const html = `<p class="mb-3 text-muted">${intro}</p><h6 class="fw-bold mb-2">${summaryTitle}</h6><div>${sectionsHtml}</div>`;
    Swal.fire({
      icon: "warning",
      title: t("bulkProductUpdate.applyConfirmTitle"),
      html,
      showCancelButton: true,
      confirmButtonText: t("bulkProductUpdate.applyConfirmConfirm"),
      cancelButtonText: t("bulkProductUpdate.applyConfirmCancel"),
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: "42rem",
      customClass: { popup: "swal2-popup-compact swal2-popup-apply-confirm" },
      scrollbarPadding: false,
      target: typeof document !== "undefined" ? document.body : undefined,
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container && typeof container.scrollIntoView === "function") {
          container.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      },
    }).then((result) => {
      if (result.isConfirmed) handleApplyChanges();
    });
  };

  useEffect(() => {
    if (step === 2 && step2CardRef.current) {
      step2CardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const backToStep1 = () => {
    setStep(1);
  };

  if (!authData?.store_id || !authData?.access_token) {
    return (
      <div className="container-fluid py-1">
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">
            {t("bulkProductUpdate.authRequired")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-1">
      {/* Page title (same style as Alt Text Generator) */}
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 fw-bold">{t("bulkProductUpdate.title")}</h4>
      </div> */}

      {/* Section tabs: card-style like Alt Text Generator */}
      <div className="mb-4 bulk-product-editor-tabs" role="tablist" aria-label={t("bulkProductUpdate.sectionTabsLabel")}>
        <div className="d-flex gap-3 flex-wrap">
          <button
            type="button"
            role="tab"
            aria-selected={sectionTab === SECTION_TAB_BULK_UPDATE}
            onClick={() => setSectionTab(SECTION_TAB_BULK_UPDATE)}
            className="flex-grow-1 py-2 px-4 rounded-3 border-0 fw-bold d-flex flex-column align-items-center justify-content-center transition-all position-relative"
            style={{
              background: sectionTab === SECTION_TAB_BULK_UPDATE ? "linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary-dark) 100%)" : "#f8f9fa",
              color: sectionTab === SECTION_TAB_BULK_UPDATE ? "#ffffff" : "#495057",
              fontSize: "1.1rem",
              cursor: "pointer",
              boxShadow: sectionTab === SECTION_TAB_BULK_UPDATE ? "0 8px 24px rgba(12, 44, 85, 0.35)" : "0 2px 4px rgba(0, 0, 0, 0.05)",
              border: sectionTab === SECTION_TAB_BULK_UPDATE ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid transparent",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              minWidth: "140px",
            }}
          >
            <div style={{ fontSize: "2rem" }}>
              <Package size={28} strokeWidth={2} />
            </div>
            <div style={{ marginBottom: "0.25rem" }}>{t("bulkProductUpdate.tabBulkUpdate")}</div>
            <small style={{ fontSize: "0.8rem", opacity: 0.9, fontWeight: "normal" }}>{t("bulkProductUpdate.tabBulkUpdateSubtitle")}</small>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sectionTab === SECTION_TAB_ROLLBACK}
            onClick={() => setSectionTab(SECTION_TAB_ROLLBACK)}
            className="flex-grow-1 py-2 px-4 rounded-3 border-0 fw-bold d-flex flex-column align-items-center justify-content-center transition-all position-relative"
            style={{
              background: sectionTab === SECTION_TAB_ROLLBACK ? "linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary-dark) 100%)" : "#f8f9fa",
              color: sectionTab === SECTION_TAB_ROLLBACK ? "#ffffff" : "#495057",
              fontSize: "1.1rem",
              cursor: "pointer",
              boxShadow: sectionTab === SECTION_TAB_ROLLBACK ? "0 8px 24px rgba(12, 44, 85, 0.35)" : "0 2px 4px rgba(0, 0, 0, 0.05)",
              border: sectionTab === SECTION_TAB_ROLLBACK ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid transparent",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              minWidth: "140px",
            }}
          >
            <div style={{ fontSize: "2rem" }}>
              <Undo2 size={28} strokeWidth={2} />
            </div>
            <div style={{ marginBottom: "0.25rem" }}>{t("bulkProductUpdate.tabRollback")}</div>
            <small style={{ fontSize: "0.8rem", opacity: 0.9, fontWeight: "normal" }}>{t("bulkProductUpdate.tabRollbackSubtitle")}</small>
          </button>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .bulk-product-editor-tabs .d-flex.gap-3 {
              flex-direction: column;
              gap: 0.75rem !important;
            }
            .bulk-product-editor-tabs .flex-grow-1 {
              width: 100%;
            }
          }
        `}</style>
      </div>

      {sectionTab === SECTION_TAB_ROLLBACK ? (
        <>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-3">{t("bulkProductUpdate.rollbackTitle")}</h5>

              {/* Filters */}
              <div className="d-flex flex-wrap align-items-end gap-3 mb-3 p-3 rounded border bg-light">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label small mb-0 text-nowrap">{t("bulkProductUpdate.rollbackFilterDateFrom")}</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ maxWidth: "160px" }}
                    value={rollbackDateFrom}
                    onChange={(e) => setRollbackDateFrom(e.target.value)}
                    aria-label={t("bulkProductUpdate.rollbackFilterDateFrom")}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label small mb-0 text-nowrap">{t("bulkProductUpdate.rollbackFilterDateTo")}</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ maxWidth: "160px" }}
                    value={rollbackDateTo}
                    onChange={(e) => setRollbackDateTo(e.target.value)}
                    aria-label={t("bulkProductUpdate.rollbackFilterDateTo")}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label small mb-0 text-nowrap">{t("bulkProductUpdate.rollbackFilterNote")}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("bulkProductUpdate.rollbackFilterNotePlaceholder")}
                    style={{ maxWidth: "220px" }}
                    value={rollbackNoteSearch}
                    onChange={(e) => setRollbackNoteSearch(e.target.value)}
                    aria-label={t("bulkProductUpdate.rollbackFilterNote")}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label small mb-0 text-nowrap">{t("bulkProductUpdate.rollbackFilterSection")}</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: "160px" }}
                    value={rollbackSectionFilter}
                    onChange={(e) => setRollbackSectionFilter(e.target.value)}
                    aria-label={t("bulkProductUpdate.rollbackFilterSection")}
                  >
                    <option value="__all__">{t("bulkProductUpdate.rollbackFilterAllSections")}</option>
                    {ROLLBACK_SECTION_IDS.map((id) => {
                      const section = STEP2_SECTIONS.find((s) => s.id === id);
                      return (
                        <option key={id} value={id}>
                          {section ? t(section.labelKey) : id}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setRollbackDateFrom("");
                    setRollbackDateTo("");
                    setRollbackNoteSearch("");
                    setRollbackSectionFilter("__all__");
                  }}
                >
                  {t("bulkProductUpdate.rollbackFilterClear")}
                </button>
              </div>

              {historyLoading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <div className="spinner-border spinner-border-sm" role="status" />
                  <span>{t("bulkProductUpdate.historyLoading")}</span>
                </div>
              ) : historyBatches.length === 0 ? (
                <p className="text-muted mb-0">{t("bulkProductUpdate.rollbackNoHistory")}</p>
              ) : (() => {
                const noteLower = (rollbackNoteSearch || "").toLowerCase().trim();
                const fromTs = rollbackDateFrom ? new Date(rollbackDateFrom).setHours(0, 0, 0, 0) : null;
                const toTs = rollbackDateTo ? new Date(rollbackDateTo).setHours(23, 59, 59, 999) : null;
                const section = (rollbackSectionFilter === "__all__" || !rollbackSectionFilter ? "" : rollbackSectionFilter).trim();
                const filtered = historyBatches.filter((b) => {
                  const batchDate = new Date(b.created_at).getTime();
                  if (fromTs != null && batchDate < fromTs) return false;
                  if (toTs != null && batchDate > toTs) return false;
                  if (noteLower && (!b.meta?.note || !String(b.meta.note).toLowerCase().includes(noteLower))) return false;
                  if (section && (!Array.isArray(b.sections_applied) || !b.sections_applied.includes(section))) return false;
                  return true;
                });
                if (filtered.length === 0) {
                  return <p className="text-muted mb-0">{t("bulkProductUpdate.rollbackNoMatches")}</p>;
                }
                return (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-nowrap">{t("bulkProductUpdate.rollbackTableDate")}</th>
                          <th className="text-nowrap">{t("bulkProductUpdate.rollbackTableProducts")}</th>
                          <th className="text-nowrap">{t("bulkProductUpdate.rollbackTableSections")}</th>
                          <th className="min-w-200">{t("bulkProductUpdate.rollbackTableNote")}</th>
                          <th className="text-end text-nowrap">{t("bulkProductUpdate.rollbackTableActions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((b) => (
                          <tr key={b.id}>
                            <td className="text-nowrap">{new Date(b.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                            <td className="text-nowrap">{b.product_count} {t("bulkProductUpdate.rollbackProducts")}</td>
                            <td className="small text-muted">
                              {Array.isArray(b.sections_applied) && b.sections_applied.length > 0
                                ? b.sections_applied
                                    .map((id) => {
                                      const section = STEP2_SECTIONS.find((s) => s.id === id);
                                      return section ? t(section.labelKey) : id;
                                    })
                                    .join(", ")
                                : "—"}
                            </td>
                            <td className="small" style={{ whiteSpace: "pre-wrap", maxWidth: "320px" }}>{b.meta?.note || "—"}</td>
                            <td className="text-end">
                              {b.reverted ? (
                                <span className="badge bg-secondary">{t("bulkProductUpdate.rollbackStatusReverted")}</span>
                              ) : (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                disabled={revertingBatchId === b.id}
                                onClick={async (e) => {
                                  revertButtonRef.current = e?.currentTarget ?? null;
                                  const batchId = b.id;
                                  const count = b.product_count;
                                  const confirmResult = await Swal.fire({
                                    icon: "warning",
                                    title: t("bulkProductUpdate.revertConfirmTitle"),
                                    text: t("bulkProductUpdate.revertConfirmMessage", { count }),
                                    showCancelButton: true,
                                    confirmButtonText: t("bulkProductUpdate.revertButton"),
                                    cancelButtonText: t("bulkProductUpdate.cancel"),
                                    confirmButtonColor: "#dc3545",
                                    cancelButtonColor: "#6c757d",
                                    customClass: { popup: "swal2-popup-compact" },
                                  });
                                  if (!confirmResult.isConfirmed) return;
                                  setRevertingBatchId(batchId);
                                  try {
                                    const res = await fetch(`${apiUrl}/api/bulk-product-update/revert`, {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json" },
                                      body: JSON.stringify({ id: storeId, batch_id: batchId }),
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    setRevertingBatchId(null);
                                    setHistoryBatches((prev) => prev.map((batch) => (batch.id === batchId ? { ...batch, reverted: true } : batch)));
                                    if (res.ok) {
                                      const hasPartial = (data.failed || 0) > 0;
                                      addToast(
                                        hasPartial
                                          ? t("bulkProductUpdate.revertPartial", { reverted: data.reverted, failed: data.failed })
                                          : t("bulkProductUpdate.revertSuccess", { count: data.reverted }),
                                        hasPartial ? "info" : "success",
                                        3000,
                                        { anchor: revertButtonRef, anchorPosition: "above" }
                                      );
                                    } else {
                                      addToast(data.error || t("bulkProductUpdate.revertFailed"), "error", 3000, { anchor: revertButtonRef, anchorPosition: "above" });
                                    }
                                  } catch (e) {
                                    setRevertingBatchId(null);
                                    addToast(t("bulkProductUpdate.revertFailed"), "error", 3000, { anchor: revertButtonRef, anchorPosition: "above" });
                                  }
                                }}
                              >
                                {revertingBatchId === b.id ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                                    {t("bulkProductUpdate.reverting")}
                                  </>
                                ) : (
                                  t("bulkProductUpdate.revertButton")
                                )}
                              </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>

        </>
      ) : (
        <>
      {/* Card 1: Find and select products */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header text-white py-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: "var(--app-primary)" }}>
          <span className="fw-semibold">
            1. {t("bulkProductUpdate.step1Title")}
          </span>
        </div>
        <div
          className="card-body p-3 p-md-4 d-flex flex-column"
          style={{ maxHeight: "70vh", overflow: "hidden" }}
        >
          {/* Filters */}
          <div className="mb-3 flex-shrink-0">
            <label className="form-label small text-muted mb-1">
              {t("bulkProductUpdate.filterLabel")}
            </label>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <div className="dropdown" ref={filterDropdownRef}>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm dropdown-toggle d-flex align-items-center gap-1"
                  style={{ minWidth: "180px", justifyContent: "space-between" }}
                  onClick={(e) => { e.stopPropagation(); setFilterDropdownOpen(!filterDropdownOpen); }}
                  aria-expanded={filterDropdownOpen}
                >
                  <span className="text-start flex-grow-1">
                    {t(FILTER_TYPES.find((f) => f.id === selectedFilterType)?.labelKey ?? "bulkProductUpdate.filterByKeyword")}
                  </span>
                </button>
                <ul
                  className={`dropdown-menu ${filterDropdownOpen ? "show" : ""}`}
                  style={{ minWidth: "260px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <li className="dropdown-header small text-muted px-3 py-2">
                    {t("bulkProductUpdate.addFilterToQuery")}
                  </li>
                  {FILTER_TYPES.map((ft) => (
                    <li key={ft.id}>
                      <button
                        type="button"
                        className={`dropdown-item btn btn-sm ${selectedFilterType === ft.id ? "active" : ""}`}
                        onClick={() => {
                          setSelectedFilterType(ft.id);
                          setFilterDropdownOpen(false);
                        }}
                      >
                        {t(ft.labelKey)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <span className="small text-muted px-2 align-self-center" style={{ minWidth: "60px" }}>
                {t(FILTER_TYPES.find((f) => f.id === selectedFilterType)?.valueLabelKey ?? "bulkProductUpdate.filterValueKeyword")}
              </span>
              {selectedFilterType === "category" ? (
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: "220px" }}
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">{t("bulkProductUpdate.allCategories")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : selectedFilterType === "price" ? (
                <>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    style={{ maxWidth: "100px" }}
                    placeholder={t("bulkProductUpdate.filterPlaceholderPriceMin")}
                    value={filterInputValue}
                    onChange={(e) => setFilterInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
                  />
                  <span className="text-muted small">–</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="form-control form-control-sm"
                    style={{ maxWidth: "100px" }}
                    placeholder={t("bulkProductUpdate.filterPlaceholderPriceMax")}
                    value={filterPriceToValue}
                    onChange={(e) => setFilterPriceToValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
                  />
                </>
              ) : selectedFilterType === "availability" ? (
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: "160px" }}
                  value={inStockApplied === null ? "" : inStockApplied ? "true" : "false"}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInStockApplied(val === "" ? null : val === "true");
                    setCurrentPage(1);
                  }}
                >
                  <option value="">{t("bulkProductUpdate.filterAvailabilityAny")}</option>
                  <option value="true">{t("bulkProductUpdate.filterAvailabilityInStock")}</option>
                  <option value="false">{t("bulkProductUpdate.filterAvailabilityOutOfStock")}</option>
                </select>
              ) : selectedFilterType === "enableDisable" ? (
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: "160px" }}
                  value={enabledApplied === null ? "" : enabledApplied ? "true" : "false"}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEnabledApplied(val === "" ? null : val === "true");
                    setCurrentPage(1);
                  }}
                >
                  <option value="">{t("bulkProductUpdate.filterEnabledAny")}</option>
                  <option value="true">{t("bulkProductUpdate.filterEnabledYes")}</option>
                  <option value="false">{t("bulkProductUpdate.filterEnabledNo")}</option>
                </select>
              ) : selectedFilterType === "creationDate" || selectedFilterType === "modificationDate" ? (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="d-flex align-items-center gap-1">
                    <span className="small text-muted">{t("bulkProductUpdate.filterDateFrom")}</span>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "150px" }}
                      value={selectedFilterType === "creationDate" ? createdFromApplied : updatedFromApplied}
                      onChange={(e) => {
                        const val = e.target.value || "";
                        if (selectedFilterType === "creationDate") setCreatedFromApplied(val);
                        else setUpdatedFromApplied(val);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span className="small text-muted">{t("bulkProductUpdate.filterDateTo")}</span>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "150px" }}
                      value={selectedFilterType === "creationDate" ? createdToApplied : updatedToApplied}
                      onChange={(e) => {
                        const val = e.target.value || "";
                        if (selectedFilterType === "creationDate") setCreatedToApplied(val);
                        else setUpdatedToApplied(val);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              ) : selectedFilterType === "attribute" ? (
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted" style={{ minWidth: "64px" }}>{t("bulkProductUpdate.filterAttributeLabel")}</span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "140px" }}
                      placeholder={t("bulkProductUpdate.filterAttributePlaceholder")}
                      value={filterInputValue}
                      onChange={(e) => setFilterInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted" style={{ minWidth: "64px" }}>{t("bulkProductUpdate.filterValueLabel")}</span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "140px" }}
                      placeholder={t("bulkProductUpdate.filterValuePlaceholder")}
                      value={filterAttributeValue}
                      onChange={(e) => setFilterAttributeValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
                    />
                  </div>
                </div>
              ) : (
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: "280px" }}
                  placeholder={FILTER_TYPES.find((f) => f.id === selectedFilterType)?.placeholderKey
                    ? t(FILTER_TYPES.find((f) => f.id === selectedFilterType).placeholderKey)
                    : t("bulkProductUpdate.searchPlaceholder")}
                  value={filterInputValue}
                  onChange={(e) => setFilterInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddFilter()}
                />
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddFilter}
                disabled={loading}
              >
                {t("bulkProductUpdate.addButton")}
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={clearFilters}
                >
                  {t("bulkProductUpdate.clearFilters")}
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={() => fetchProducts()}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <RefreshCw size={14} />
                )}
                {t("bulkProductUpdate.refresh")}
              </button>
            </div>
          </div>

          {/* Select / Sort bar */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 flex-shrink-0">
            <div className="d-flex flex-wrap align-items-center gap-2 small">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={selectPage}
              >
                {t("bulkProductUpdate.selectPage")}
              </button>
              <span className="text-muted">|</span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={selectAllMatchedFull}
              >
                {t("bulkProductUpdate.selectAllMatched")}
              </button>
              <span className="text-muted ms-2">
                {t("bulkProductUpdate.sortBy")}
                <select
                  className="form-select form-select-sm d-inline-block ms-1"
                  style={{ width: "auto" }}
                  value={sortField}
                  onChange={(e) => {
                    setSortField(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value || "default"} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
                {sortField && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1"
                    onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                  >
                    {sortDirection === "asc" ? <SortAsc size={14} /> : <SortDesc size={14} />}
                  </button>
                )}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2 small">
              <span className="text-muted">{t("bulkProductUpdate.productsPerPage")}</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 flex-shrink-0">
              <nav aria-label="Pagination">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {Array.from({ length: Math.min(9, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 9) pageNum = i + 1;
                    else if (currentPage <= 5) pageNum = i + 1;
                    else if (currentPage >= totalPages - 4) pageNum = totalPages - 8 + i;
                    else pageNum = currentPage - 4 + i;
                    return (
                      <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
              <span className="small text-muted">
                {t("bulkProductUpdate.pageOf", { current: currentPage, total: totalPages })}
              </span>
            </div>
          )}

          {/* Product grid (scrollable area) */}
          <div
            className="mb-3 flex-grow-1 overflow-auto"
            style={{ minHeight: 0 }}
          >
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2 text-muted small">{t("bulkProductUpdate.loadingProducts")}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Package size={48} className="opacity-50 mb-2" />
                <p className="mb-0">{t("bulkProductUpdate.noProducts")}</p>
              </div>
            ) : (
              <div className="row g-2">
                {products.map((p) => {
                const imgUrl = productImageUrl(p);
                const checked = selectedIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    className="col-6 col-md-4 col-lg-3 col-xl-2"
                  >
                    <div
                      className={`card h-100 border position-relative ${checked ? "border-primary bg-light" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSelect(p.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && toggleSelect(p.id)}
                    >
                      {uploadingProductId === p.id && (
                        <div
                          className="position-absolute top-0 start-0 end-0 bottom-0 rounded d-flex flex-column align-items-center justify-content-center gap-2"
                          style={{
                            zIndex: 10,
                            backgroundColor: "rgba(255,255,255,0.85)",
                            fontSize: "0.8rem"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="spinner-border text-primary" role="status" />
                          <span className="text-muted fw-medium">{t("bulkProductUpdate.uploading")}</span>
                        </div>
                      )}
                      <div className="card-body p-2 text-center position-relative">
                        <div className="position-relative mb-1">
                          <input
                            type="checkbox"
                            className="form-check-input position-absolute top-0 start-0"
                            style={{ zIndex: 1 }}
                            checked={checked}
                            onChange={() => toggleSelect(p.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div
                            ref={uploadMenuProductId === p.id ? uploadMenuContainerRef : undefined}
                            className="position-absolute top-0 end-0 d-flex flex-column align-items-end flex-column-reverse"
                            style={{ zIndex: 2 }}
                          >
                            <button
                              type="button"
                              className="btn btn-sm p-1 rounded shadow-sm flex-shrink-0"
                              style={{ backgroundColor: "rgba(253, 126, 20, 0.9)", color: "#fff" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadMenuProductId((id) => (id === p.id ? null : p.id));
                              }}
                              aria-label={t("bulkProductUpdate.uploadProductImage")}
                            >
                              <Upload size={16} />
                            </button>
                            {uploadMenuProductId === p.id && (
                              <div
                                className="d-flex flex-column gap-1 mb-1 p-1 rounded shadow flex-shrink-0"
                                style={{ backgroundColor: "#fff", minWidth: "160px" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm w-100 text-white border-0 text-start"
                                  style={{ backgroundColor: "#fd7e14", borderRadius: "8px" }}
                                  onClick={() => triggerUpload(p.id, "productImage")}
                                >
                                  {t("bulkProductUpdate.uploadProductImage")}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm w-100 text-white border-0 text-start"
                                  style={{ backgroundColor: "#fd7e14", borderRadius: "8px" }}
                                  onClick={() => triggerUpload(p.id, "galleryImage")}
                                >
                                  {t("bulkProductUpdate.uploadGalleryImage")}
                                </button>
                                {/* <button
                                  type="button"
                                  className="btn btn-sm w-100 text-white border-0 text-start"
                                  style={{ backgroundColor: "#fd7e14", borderRadius: "8px" }}
                                  onClick={() => triggerUpload(p.id, "downloadable")}
                                >
                                  {t("bulkProductUpdate.uploadDownloadableFile")}
                                </button> */}
                              </div>
                            )}
                          </div>
                          <div
                            className="rounded bg-light d-flex align-items-center justify-content-center"
                            style={{ height: "80px" }}
                          >
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt=""
                                className="rounded"
                                style={{ maxHeight: "80px", width: "auto", objectFit: "contain" }}
                              />
                            ) : (
                              <ImageIcon size={32} className="text-muted" />
                            )}
                          </div>
                        </div>
                        <div className="small text-truncate" title={p.name}>
                          {p.sku ? `[${p.sku}] ` : ""}
                          {p.name || p.id}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>

          {/* Bottom bar: selected count + Proceed (step 1) */}
          <div className="border-top pt-3 mt-2 flex-shrink-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="text-muted small">
                {t("bulkProductUpdate.selectedCount", { count: selectedIds.size })}
              </span>
              <button
                type="button"
                className="btn btn-primary"
                disabled={selectedIds.size === 0}
                onClick={goToStep2}
              >
                {t("bulkProductUpdate.proceed")}
              </button>
            </div>
          </div>
          <input
            ref={productImageInputRef}
            type="file"
            accept="image/*"
            className="d-none"
            onChange={(e) => handleUploadFile(e, "productImage")}
          />
          <input
            ref={galleryImageInputRef}
            type="file"
            accept="image/*"
            className="d-none"
            onChange={(e) => handleUploadFile(e, "galleryImage")}
          />
          <input
            ref={downloadableFileInputRef}
            type="file"
            className="d-none"
            onChange={(e) => handleUploadFile(e, "downloadable")}
          />
        </div>
      </div>

      {/* Card 2: Choose what to change (accordion sections) */}
      <div ref={step2CardRef} className="card border-0 shadow-sm mb-4 overflow-hidden">
        <div className="card-header text-white py-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: "var(--app-primary)" }}>
          <span className="fw-semibold">
            2. {t("bulkProductUpdate.step2Title")}
          </span>
          {step === 2 && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={backToStep1}
            >
              {t("bulkProductUpdate.backToStep1")}
            </button>
          )}
        </div>
        <div className="card-body p-0">
          {step === 1 ? (
            <div className="p-4 text-center text-muted">
              <p className="mb-0 small">{t("bulkProductUpdate.step2Hint")}</p>
            </div>
          ) : (
            <>
              <div
                className="alert alert-danger border-0 rounded-0 mb-0 px-4 py-3 d-flex align-items-center gap-2"
                style={{ backgroundColor: "rgba(220, 53, 69, 0.08)" }}
                role="alert"
              >
                <span className="fw-bold text-danger" style={{ fontSize: "0.95rem" }}>
                  {t("bulkProductUpdate.step2Caution")}
                </span>
              </div>

              <div className="p-3">
                {STEP2_SECTIONS.map((section) => {
                  const isOpen = step2OpenId === section.id;
                  const Icon = section.icon;
                  return (
                    <div
                      key={section.id}
                      className="rounded-3 mb-2 overflow-hidden"
                      style={{
                        border: "1px solid var(--bs-border-color)",
                        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                        boxShadow: isOpen ? "0 4px 12px rgba(0,0,0,0.06)" : "none",
                        borderColor: isOpen ? "var(--app-primary)" : undefined,
                      }}
                    >
                      <button
                        type="button"
                        className="w-100 d-flex align-items-center gap-3 p-3 border-0 bg-white text-start text-dark text-decoration-none"
                        style={{
                          cursor: "pointer",
                          minHeight: "56px",
                          borderLeft: isOpen ? "4px solid var(--app-primary)" : "4px solid transparent",
                        }}
                        onClick={() => setStep2OpenId((id) => (id === section.id ? null : section.id))}
                        aria-expanded={isOpen}
                        aria-controls={`step2-panel-${section.id}`}
                        id={`step2-heading-${section.id}`}
                      >
                        <span
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{
                            width: "36px",
                            height: "36px",
                            background: isOpen
                              ? "linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary-dark) 100%)"
                              : "linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)",
                            color: isOpen ? "#fff" : "#495057",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {isOpen ? <Minus size={18} strokeWidth={2.5} /> : <Plus size={18} strokeWidth={2.5} />}
                        </span>
                        <span className="d-flex align-items-center gap-2 flex-grow-1">
                          <Icon size={20} className="text-muted flex-shrink-0" />
                          <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                            {t(section.labelKey)}
                          </span>
                          {section.id === "properties" && !isOpen && propertiesSelectedCount > 0 && (
                            <span
                              className="badge rounded-pill flex-shrink-0"
                              style={{
                                backgroundColor: "rgba(253, 126, 20, 0.2)",
                                color: "#c2410c",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            >
                              {propertiesSelectedCount} {propertiesSelectedCount === 1 ? t("bulkProductUpdate.change") : t("bulkProductUpdate.changes")}
                            </span>
                          )}
                          {section.id === "seo" && !isOpen && seoSelectedCount > 0 && (
                            <span
                              className="badge rounded-pill flex-shrink-0"
                              style={{
                                backgroundColor: "rgba(253, 126, 20, 0.2)",
                                color: "#c2410c",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            >
                              {seoSelectedCount} {seoSelectedCount === 1 ? t("bulkProductUpdate.change") : t("bulkProductUpdate.changes")}
                            </span>
                          )}
                          {section.id === "replaceText" && !isOpen && replaceTextRules.length > 0 && (
                            <span
                              className="badge rounded-pill flex-shrink-0"
                              style={{
                                backgroundColor: "rgba(253, 126, 20, 0.2)",
                                color: "#c2410c",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            >
                              {replaceTextRules.length} {replaceTextRules.length === 1 ? t("bulkProductUpdate.change") : t("bulkProductUpdate.changes")}
                            </span>
                          )}
                        </span>
                        <ChevronDown
                          size={20}
                          className="text-muted flex-shrink-0"
                          style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </button>
                      <div
                        id={`step2-panel-${section.id}`}
                        role="region"
                        aria-labelledby={`step2-heading-${section.id}`}
                        className="border-top"
                        style={{
                          maxHeight: isOpen ? (section.id === "properties" || section.id === "pricing" || section.id === "shipping" || section.id === "attributes" || section.id === "categories" || section.id === "clearGallery" || section.id === "related" || section.id === "seo" || section.id === "replaceText" ? "2000px" : "200px") : "0",
                          overflow: "hidden",
                          transition: "max-height 0.3s ease-out",
                        }}
                      >
                        <div
                          className="p-3 bg-light"
                          style={{ backgroundColor: "rgba(248, 249, 250, 0.8)" }}
                        >
                          {section.id === "properties" ? (
                            <PropertiesForm
                              t={t}
                              propertySelected={propertySelected}
                              propertyValues={propertyValues}
                              setPropertyChecked={setPropertyChecked}
                              setPropertyValue={setPropertyValue}
                              propertiesSelectedCount={propertiesSelectedCount}
                              onClearAll={clearPropertiesSection}
                            />
                          ) : section.id === "pricing" ? (
                            <PricingForm
                              t={t}
                              modificationDetailsOn={pricingModificationDetailsOn}
                              setModificationDetailsOn={setPricingModificationDetailsOn}
                              roundingMode={pricingRoundingMode}
                              setRoundingMode={setPricingRoundingMode}
                              roundingResolution={pricingRoundingResolution}
                              setRoundingResolution={setPricingRoundingResolution}
                              pricingRule={pricingRule}
                              setPricingRule={setPricingRule}
                              saveOldPriceToCompareAt={pricingSaveOldPriceToCompareAt}
                              setSaveOldPriceToCompareAt={setPricingSaveOldPriceToCompareAt}
                              revertSalesPrice={pricingRevertSalesPrice}
                              setRevertSalesPrice={setPricingRevertSalesPrice}
                              wholesaleRules={pricingWholesaleRules}
                              setWholesaleRules={setPricingWholesaleRules}
                              clearWholesale={pricingClearWholesale}
                              setClearWholesale={setPricingClearWholesale}
                            />
                          ) : section.id === "shipping" ? (
                            <ShippingForm
                              t={t}
                              shippingConfig={shippingConfig}
                              setShippingConfig={setShippingConfig}
                              shippingStoreFixedFee={shippingStoreFixedFee}
                              setShippingStoreFixedFee={setShippingStoreFixedFee}
                              shippingStoreFixedFeeAmount={shippingStoreFixedFeeAmount}
                              setShippingStoreFixedFeeAmount={setShippingStoreFixedFeeAmount}
                              shippingSpecifyMode={shippingSpecifyMode}
                              setShippingSpecifyMode={setShippingSpecifyMode}
                              shippingOptions={shippingOptions}
                              shippingSelectedMethodIds={shippingSelectedMethodIds}
                              setShippingSelectedMethodIds={setShippingSelectedMethodIds}
                              shippingFixedRateAmount={shippingFixedRateAmount}
                              setShippingFixedRateAmount={setShippingFixedRateAmount}
                            />
                          ) : section.id === "attributes" ? (
                            <AttributesForm
                              t={t}
                              attributeRules={attributeRules}
                              setAttributeRules={setAttributeRules}
                              attributesImportModalOpen={attributesImportModalOpen}
                              setAttributesImportModalOpen={setAttributesImportModalOpen}
                              attributesSearchKeyword={attributesSearchKeyword}
                              setAttributesSearchKeyword={setAttributesSearchKeyword}
                              attributesSearchResults={attributesSearchResults}
                              setAttributesSearchResults={setAttributesSearchResults}
                              attributesSearchLoading={attributesSearchLoading}
                              setAttributesSearchLoading={setAttributesSearchLoading}
                              attributesSelectedProductId={attributesSelectedProductId}
                              setAttributesSelectedProductId={setAttributesSelectedProductId}
                              attributesSearchAttempted={attributesSearchAttempted}
                              setAttributesSearchAttempted={setAttributesSearchAttempted}
                              apiUrl={apiUrl}
                              authData={authData}
                            />
                          ) : section.id === "categories" ? (
                            <CategoriesForm
                              t={t}
                              categories={categories}
                              categoriesLoading={categoriesLoading}
                              categoryIdsToAssign={categoryIdsToAssign}
                              setCategoryIdsToAssign={setCategoryIdsToAssign}
                              categoryAction={categoryAction}
                              setCategoryAction={setCategoryAction}
                              categoryPickerModalOpen={categoryPickerModalOpen}
                              setCategoryPickerModalOpen={setCategoryPickerModalOpen}
                            />
                          ) : section.id === "clearGallery" ? (
                            <ClearGalleryForm
                              t={t}
                              clearGalleryImages={clearGalleryImages}
                              setClearGalleryImages={setClearGalleryImages}
                              clearGalleryFiles={clearGalleryFiles}
                              setClearGalleryFiles={setClearGalleryFiles}
                            />
                          ) : section.id === "seo" ? (
                            <SeoForm
                              t={t}
                              propertySelected={propertySelected}
                              propertyValues={propertyValues}
                              setPropertyChecked={setPropertyChecked}
                              setPropertyValue={setPropertyValue}
                            />
                          ) : section.id === "replaceText" ? (
                            <ReplaceTextForm
                              t={t}
                              rules={replaceTextRules}
                              setRules={setReplaceTextRules}
                            />
                          ) : section.id === "related" ? (
                            <RelatedProductsForm
                              t={t}
                              deleteAllRelatedProducts={deleteAllRelatedProducts}
                              setDeleteAllRelatedProducts={setDeleteAllRelatedProducts}
                              relatedProductsToAdd={relatedProductsToAdd}
                              setRelatedProductsToAdd={setRelatedProductsToAdd}
                              apiUrl={apiUrl}
                              authData={authData}
                              productImageUrl={productImageUrl}
                            />
                          ) : (
                            <p className="mb-0 small text-muted">
                              {t("bulkProductUpdate.sectionComingSoon")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 pt-0">
                <label className="form-label small text-muted mb-1">{t("bulkProductUpdate.applyNoteLabel")}</label>
                <textarea
                  className="form-control form-control-sm mb-2"
                  rows={2}
                  placeholder={t("bulkProductUpdate.applyNotePlaceholder")}
                  value={applyNote}
                  onChange={(e) => setApplyNote(e.target.value)}
                  aria-label={t("bulkProductUpdate.applyNoteLabel")}
                />
                {applySuccessMessage && (
                  <div
                    className="alert alert-success mb-3 mb-md-2 py-2 px-3 d-flex align-items-center gap-2"
                    role="status"
                    style={{ fontSize: "0.95rem" }}
                  >
                    <span className="flex-shrink-0" aria-hidden>✓</span>
                    <span>{applySuccessMessage}</span>
                  </div>
                )}
                <button
                  ref={applyButtonRef}
                  type="button"
                  className="btn w-100 py-3 fw-bold border-0 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    background: "linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%)",
                    color: "#fff",
                    fontSize: "1rem",
                    boxShadow: "0 4px 14px rgba(220, 53, 69, 0.4)",
                  }}
                  disabled={!canApply}
                  onClick={showApplyConfirmSwal}
                >
                  {applyingChanges ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      {t("bulkProductUpdate.applying")}
                    </>
                  ) : (
                    t("bulkProductUpdate.applyChangesNow")
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default BulkProductUpdate;
